import {
  InfoOutlined,
  ColumnHeightOutlined,
  ColumnWidthOutlined,
  FilterOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MenuOutlined,
  RetweetOutlined,
  SwapOutlined,
  TeamOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { levelRank } from '@shared/levels';
import type { ExecutiveTreeNode, OrgNode, Position, Summary } from '@shared/types';
import { useQuery } from '@tanstack/react-query';
import { api, type SessionUser } from '../api/client.js';
import {
  FILL_BANDS,
  OrgFlow,
  RETIRE_WATCH_YEARS,
  bandOf,
  retireMark,
  type OrgFlowDir,
  type OrgFlowMode,
} from './OrgFlow.js';
import { PanelGrip, PositionListPanel, PositionSidePanel, type PositionListMode } from './PositionListPanel.js';
import { OrgPositions } from './OrgPositions.js';
import { PositionModal } from './PositionModal.js';
import { RetirementWaveModal } from './RetirementWaveModal.js';
import { RotationModal } from './RotationModal.js';
import { RotationPlanModal } from './RotationPlanModal.js';
import { RetirementRosterModal } from './RetirementRosterModal.js';
import { AuthorityModal } from './AuthorityModal.js';
import { ThemeToggle } from './ThemeToggle.js';
import { ExecutiveRosterModal } from './ExecutiveRosterModal.js';
import { KookitModal } from './KookitModal.js';
import { KookitLauncher } from './KookitLauncher.js';
import { ExportButtons } from './ExportButtons.js';
import { ownerLine } from './SiteFooter.js';
import type { ExportTable } from '../lib/exportTable.js';

/**
 * ผังกำลังคนแบบเต็มพื้นที่ — โครงหน้าสำหรับผู้บริหาร
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมผังถึงได้ทั้งหน้า ไม่ใช่กล่องหนึ่งในหน้าที่เลื่อนได้
 * ────────────────────────────────────────────────────────────────
 *
 * เดิมผังเป็นกล่องสูง 74vh อยู่กลางหน้าที่เลื่อนลงไปเจอตารางต่อ
 * ปัญหาคือผังเป็นของที่ต้อง "กวาดสายตาทั้งผืน" ไม่ใช่ของที่อ่านทีละบรรทัด
 * พอมีขอบบนขอบล่างมาบีบ ผู้ใช้ต้องซูมออกจนอ่านชื่อหน่วยไม่ออก
 * หรือไม่ก็ต้องเลื่อนหน้าไปมาระหว่างผังกับปุ่มที่ใช้คุมผัง
 *
 * โครงนี้จึงให้ผังกินพื้นที่ที่เหลือทั้งหมด แล้ววางของที่ต้องใช้พร้อมผังไว้รอบ ๆ แบบตรึงที่
 *   บนสุด   ตัวเลขรวม — ตอบ "ภาพรวมตอนนี้เป็นยังไง" โดยไม่ต้องเลื่อน
 *   ถัดมา   ปุ่มโหมด — สลับคำถามที่กำลังถามกับผังใบเดิม
 *   ขวา     แผงรายละเอียดของหน่วยที่กด
 *   ล่างซ้าย คำอธิบายสัญลักษณ์ ยุบเก็บได้
 *
 * ⚠ แผงขวาเป็นคอลัมน์ของ grid ไม่ใช่กล่องลอยทับผัง
 * ตอนเปิดแผง ผังจะแคบลงแล้วจัดตัวเองใหม่ให้พอดีพื้นที่ที่เหลือ (ดู ResizeObserver ใน OrgFlow)
 * ถ้าทำเป็นกล่องลอย กิ่งที่ผู้ใช้เพิ่งกดจะไปอยู่ใต้แผงพอดี ซึ่งเป็นกิ่งเดียวที่เขาอยากดู
 */

/**
 * โหมดของผังที่มีปุ่มให้กด
 *
 * ⚠ เอา 'retirement' (การเกษียณ) · 'forecast' (ผลกระทบ) · 'succession'
 * (ตำแหน่งระดับสูงที่จะว่าง) ออกจากแถบปุ่มตามที่ผู้ใช้ขอ
 * แต่ **ไม่ได้ลบโค้ดของสามโหมดนั้น** — ยังอยู่ครบใน OrgFlow.tsx และยัง typecheck ตามปกติ
 *
 * ทำแบบเดียวกับหน้าที่ถูกยกออกจากเมนูใน Console.tsx ด้วยเหตุผลเดียวกัน:
 * โค้ดที่ถูกลบหรือถูก comment ทิ้ง คือโค้ดที่ไม่มีใคร typecheck ให้อีก
 * วันที่อยากเอากลับมาจะพบว่ามันพังไปแล้วโดยไม่มีใครรู้ว่าพังตั้งแต่เมื่อไร
 * เอากลับ = เพิ่มรายการกลับเข้า MODES แค่นั้น ไม่ต้องรื้ออะไรเพิ่ม
 *
 * ⚠ ข้อมูลการเกษียณไม่ได้หายไปจากระบบ ยังเข้าถึงได้จาก
 *   · กล่องตัวเลข "จะเกษียณใน 5 ปี" บนแถบบน (กดแล้วกรองผังได้)
 *   · แผงรายละเอียดหน่วยงาน ซึ่งแจกแจงผู้เกษียณรายปีงบประมาณ
 * ถ้าวันหนึ่งเอาทางเข้าพวกนั้นออกอีก ต้องเอาโหมดนี้กลับมา ไม่งั้นจะไม่เหลือทางดูเลย
 *
 * ⚠ ตอนนี้ว่างเปล่า ไม่เหลือปุ่มโหมดสักใบ ผังจึงค้างอยู่ที่ 'staffing' ตลอด
 *   แถวที่เห็นบนแคนวาสเหลือแต่ปุ่มเครื่องมือ ซึ่งไม่ใช่โหมดของผัง
 *
 * ⚠ ที่หายไปพร้อมปุ่มคือคำอธิบายว่าตัวเลขบนการ์ดคำนวณจากอะไร
 *   (เดิมอยู่ใน data-tip ของปุ่ม "อัตรากำลัง")
 *   ตอนนี้พึ่งสามอย่างแทน — caption "กรอบอัตรากำลัง" บนการ์ดเอง
 *   บรรทัดล่างที่บอกว่าง/เกษียณ และแถบคำอธิบายสีมุมล่างซ้าย
 *   ถ้าวันหนึ่งเอาสามอย่างนั้นออกอีก ต้องหาที่ให้คำอธิบายก่อน ไม่ใช่ปล่อยให้หายไปเฉย ๆ
 */
const MODES: ReadonlyArray<{ id: OrgFlowMode; label: string; hint: string }> = [];

/**
 * จอแคบอยู่ไหม — ใช้ตัดสินว่าจะแสดงแถบปุ่มเจ็ดใบ หรือยุบเป็นเมนูปุ่มเดียว
 *
 * ⚠ อันนี้ต้องตามการหมุนจอ ต่างจากทิศทางผัง (DIR_KEY) ที่อ่านครั้งเดียวตอน mount
 * เพราะถ้าไม่ตาม คนที่หมุนมือถือเป็นแนวนอนจะได้เมนูยุบทั้งที่มีที่ว่างเหลือครึ่งจอ
 * ส่วนคนที่หมุนกลับเป็นแนวตั้งจะได้ปุ่มเจ็ดใบล้นออกนอกจอ
 *
 * ทิศทางผังไม่ตามการหมุน เพราะการจัดผังใหม่ทั้งผืนทำให้หากิ่งที่กำลังดูอยู่ไม่เจอ
 * ส่วนแถบปุ่มเป็นแค่เปลือก ยุบ-กางแล้วไม่มีอะไรหาย
 */
function useNarrow(query: string): boolean {
  const [narrow, setNarrow] = useState(() => {
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(query);
    } catch {
      return;
    }
    setNarrow(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return narrow;
}

/** ความกว้างที่ปุ่มเจ็ดใบเริ่มอยู่แถวเดียวไม่ได้ */
const NARROW = '(max-width: 760px)';

/** คีย์ที่ใช้จำทิศทางผังที่ผู้ใช้เลือก — ขึ้นต้นด้วยชื่อระบบกันชนกับแอปอื่นบนโดเมนเดียวกัน */
const DIR_KEY = 'yana.orgflow.dir';

const num = (n: number) => n.toLocaleString('th-TH');

export interface OrgCanvasProps {
  tree: OrgNode[];
  summary: Summary;
  /**
   * ส่งผู้ใช้เข้ามา = ใช้เป็น "ทั้งหน้าจอของระบบ" ไม่ใช่กล่องในหน้าอื่น
   *
   * โหมดนี้จะเพิ่มแถบชื่อระบบด้านบนกับแถบที่มาข้อมูลด้านล่างเข้ามาเอง
   * เพราะไม่มีเปลือกของหน้าอื่นมาห่อให้อีกแล้ว และสองอย่างนี้ขาดไม่ได้
   * โดยเฉพาะแถบล่าง ซึ่งเป็นการเปิดเผยที่มาข้อมูลที่ต้องมีตลอดเวลา
   */
  user?: SessionUser;
  onLogout?: () => void;
}

export function OrgCanvas({ tree, summary, user, onLogout }: OrgCanvasProps) {
  const [mode, setMode] = useState<OrgFlowMode>('staffing');
  /**
   * ทิศทางการวางผัง — ระบบเลือกให้ตามความกว้างจอ ไม่มีปุ่มให้ผู้ใช้สลับแล้ว
   *
   * ⚠ ปุ่ม "บนลงล่าง / ซ้ายไปขวา" ถูกเอาออกตามที่ผู้ใช้ขอ เพื่อคืนความสูงให้ผัง
   * แต่ตัวเลือกทิศยังต้องมีอยู่ในโค้ด เพราะบนมือถือแนวนอนมีแค่ ~390px
   * ถ้าบังคับบนลงล่าง พี่น้องสี่ใบจะเรียงข้างกันแล้วต้องย่อจนอ่านชื่อหน่วยไม่ออก
   * ซ้ายไปขวาจึงเป็นทางเดียวที่ใช้ได้จริงบนจอแคบ เพราะพี่น้องเรียงลงล่าง
   * ซึ่งเป็นแกนที่เลื่อนได้ไม่จำกัด
   *
   * อ่านค่าครั้งเดียวตอน mount ไม่ตามการหมุนจอ — ผังที่จัดใหม่ทั้งผืนเองกลางคัน
   * ทำให้ผู้ใช้หากิ่งที่กำลังดูอยู่ไม่เจอ
   *
   * ⚠ ปุ่มสลับทิศกลับมาแล้ว (อยู่ในแถบเครื่องมือมุมขวาบน) แต่ค่าเริ่มต้นยังเลือกให้ตามจอเหมือนเดิม
   * เพราะคนที่เปิดครั้งแรกบนมือถือไม่ควรต้องกดอะไรก่อนถึงจะอ่านผังออก
   *
   * ลำดับการตัดสินค่าเริ่มต้น
   *   1. ค่าที่ผู้ใช้เคยเลือกไว้เอง (localStorage) — ถ้าเคยเลือก ถือว่าเจตนาชัดกว่าขนาดจอ
   *   2. ไม่เคยเลือก → เดาจากความกว้างจอ
   *
   * localStorage อาจโยน exception ได้ (โหมดส่วนตัวบางเบราว์เซอร์ หรือ cookie ถูกปิด)
   * จึงต้องครอบ try/catch ทั้งตอนอ่านและตอนเขียน ห้ามปล่อยให้ทั้งหน้าพังเพราะจำค่าไม่ได้
   */
  const [dir, setDir] = useState<OrgFlowDir>(() => {
    try {
      const saved = window.localStorage.getItem(DIR_KEY);
      if (saved === 'TB' || saved === 'LR') return saved;
      return window.matchMedia('(min-width: 860px)').matches ? 'TB' : 'LR';
    } catch {
      return 'TB';
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(DIR_KEY, dir);
    } catch {
      /* จำไม่ได้ก็ไม่เป็นไร ผังยังใช้ได้ปกติ */
    }
  }, [dir]);
  const [selected, setSelected] = useState<string | null>(null);
  /* หน่วยที่กำลังเปิดบัญชีอัตราอยู่ — null คือไม่ได้เปิด
     เก็บทั้ง id (ใช้กรองแบบตรงตัว) และโหมด เพราะปุ่มคนละปุ่มต้องได้บัญชีคนละชุด */
  const [listOf, setListOf] = useState<{ id: string; mode: PositionListMode } | null>(null);
  /**
   * ความกว้างของแผงบัญชีอัตราที่ผู้ใช้ลากไว้ (px) — null คือยังไม่เคยลาก ใช้ค่าแบ่งครึ่งจาก CSS
   *
   * ⚠ เก็บไว้ที่นี่ ไม่ใช่ในตัวแผง เพราะแผงถูกถอดออกทุกครั้งที่ปิด
   *   ถ้าเก็บข้างใน ความกว้างที่ลากไว้จะหายทุกครั้งแล้วเด้งกลับครึ่งจอเอง
   *   (เหตุผลเดียวกับที่ตำแหน่งการ์ด UnitPanel ถูกเก็บไว้ที่หน้าแม่)
   */
  const [listW, setListW] = useState<number | null>(null);

  /** หน่วยงานที่กำลังเจาะดู — null คือดูทั้งกระทรวง */
  const [scope, setScope] = useState<string | null>(null);

  /**
   * กล่องตัวเลขใบที่กดอยู่ — 'total' คือไม่ได้กรองอะไร
   *
   * เก็บเป็นค่าเดียว ไม่ใช่ชุดของหลายเงื่อนไข เพราะการกรองพร้อมกันสองอย่าง
   * (เช่น มีผู้ครอง + มีคนจะเกษียณ) ผู้ใช้อ่านผลไม่ออกว่าที่เหลือคือหน่วยที่เข้าทั้งสอง
   * หรือเข้าอย่างใดอย่างหนึ่ง แล้วต้องมีตัวเลือก และ/หรือ ตามมาอีก
   */
  /*
   * null = ไม่ได้กรอง ซึ่งไม่เหมือน 'total'
   *
   * เดิมใช้ 'total' เป็นทั้งชื่อกล่องและสถานะ "ไม่กรอง" กล่องแรกจึงสว่างค้างตลอด
   * และกดออกไม่ได้ เพราะกดแล้วมันสั่งตัวเองให้เป็น 'total' อยู่ดี
   * ผู้ใช้ที่เห็นกล่องสว่างอยู่ใบเดียวย่อมอ่านว่า "ตอนนี้กรองอะไรอยู่สักอย่าง"
   * ทั้งที่ผังแสดงครบทุกหน่วย
   *
   * แยกค่าว่างออกมาแล้ว ทั้งสี่ใบจึงกดติดและกดออกได้เหมือนกันหมด
   * ส่วนการคำนวณข้างล่างยังใช้ 'total' เป็นค่าตั้งต้นเหมือนเดิม (ดู effFilter)
   */
  const [statFilter, setStatFilter] = useState<StatFilter | null>(null);

  /** ตัวกรองที่ใช้คำนวณจริง — ไม่ได้กรอง มีค่าเท่ากับดูทุกหน่วย */
  const effFilter: StatFilter = statFilter ?? 'total';

  const shell = useRef<HTMLDivElement | null>(null);
  /**
   * คำอธิบายสัญลักษณ์เปิดไว้บนจอกว้าง แต่ยุบไว้บนจอแคบ
   *
   * บนมือถือแผงนี้กว้างเกือบเท่าจอ ถ้าเปิดค้างไว้ผู้ใช้จะเห็นผังแค่เสี้ยวเดียว
   * ตั้งค่าตอน mount ครั้งเดียวพอ ไม่ต้องตามการหมุนจอ เพราะถ้าเด้งเปิด-ปิดเองตอนหมุน
   * ผู้ใช้ที่เพิ่งยุบมันไปจะเห็นมันกลับมาเองโดยไม่ได้สั่ง
   */
  /** หน้าต่างจำลองการโยกย้ายเปิดอยู่ไหม */
  const [sim, setSim] = useState(false);

  /**
   * หน้าต่างแผนหมุนเวียนกำลังคนเปิดอยู่ไหม
   *
   * แยกจาก sim ด้านบนโดยตั้งใจ ไม่ใช่แท็บในหน้าต่างเดียวกัน เพราะสองอันถามคนละคำถาม
   *   sim  เข้าทางอัตราว่างระดับสูง → ใครผ่านเกณฑ์ระยะเวลาเพื่อ "เลื่อน" ขึ้นมา
   *   rot  เข้าทางความไม่สมดุลของกำลังคน → ใครย้าย "ระดับเดิม" ข้ามหน่วยมาปิดช่องได้
   */
  const [rot, setRot] = useState(false);

  /**
   * หน้าต่างรายชื่อผู้เกษียณเปิดอยู่ไหม
   *
   * แสดงปุ่มตลอด ไม่ผูกกับโหมด "การเกษียณ" โดยตั้งใจ — "ใครจะเกษียณเมื่อไหร่"
   * เป็นหนึ่งในสี่คำถามที่ผู้บริหารถามบ่อยที่สุด ถ้าซ่อนไว้หลังโหมด
   * คนที่ไม่เคยกดโหมดนั้นจะไม่รู้เลยว่าระบบตอบให้ได้
   */
  const [ret, setRet] = useState(false);

  /**
   * หน้าต่าง "ใครเป็นคนลงนามแต่งตั้ง" เปิดอยู่ไหม
   *
   * เป็นเครื่องมือ ไม่ใช่โหมดของผัง — มันไม่ได้เปลี่ยนสิ่งที่ผังแสดง แต่ตอบคำถาม
   * ที่มักตามหลังผังทันที คือเห็นอัตราว่างระดับหนึ่งแล้วอยากรู้ว่าใครเซ็นแต่งตั้งได้
   */
  const [auth, setAuth] = useState(false);

  /**
   * บัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจแต่งตั้ง — เปิดจากไอคอนบนการ์ดกระทรวง
   *
   * ⚠ ไม่มีปุ่มบนแถบเครื่องมือโดยตั้งใจ
   * มันเป็นเรื่องของกระทรวงทั้งกระทรวง ไม่ใช่ของหน่วยใดหน่วยหนึ่ง ทางเข้าจึงควรอยู่
   * บนการ์ดกระทรวงซึ่งเป็นที่ที่ผู้ใช้กำลังมองอยู่แล้วตอนคิดเรื่องนี้
   * และแถบด้านบนก็แน่นจนผู้ใช้ทักมาแล้วสองรอบ
   */
  /**
   * ผังที่กำลังดูอยู่ — หน่วยงาน หรือ คนระดับ 9 ขึ้นไป
   *
   * ⚠ ไอคอนบนการ์ดกระทรวงเปลี่ยนคอนเซปแล้ว เดิมเปิดหน้าต่างบัญชีผู้บริหาร
   *   ตอนนี้สลับทั้งผืนผ้าใบมาเป็นผังคน ตามที่ผู้ใช้สั่ง
   *   หน้าต่างบัญชีเดิม (ExecutiveRosterModal) ยังอยู่และยังเรียกได้จากที่อื่น
   *   แต่ไม่ได้ผูกกับไอคอนนี้แล้ว
   */
  /* ⚠ บัญชีปลัด (c9plus) เข้ามาแล้วเจอผังผู้บริหารตามตำแหน่งทันที (ผู้ใช้สั่ง 19 ก.ย. 2569)
       บัญชีอื่นยังเปิดที่ผังหน่วยงานเหมือนเดิม */
  const [view, setView] = useState<'units' | 'people'>(() => (user?.view === 'c9plus' ? 'people' : 'units'));
  /** อัตราที่กดจากการ์ดคนในผังคน — โหลดแล้วเปิดหน้าต่างรายละเอียดอัตราตัวเดิม */
  const [personSeat, setPersonSeat] = useState<string | null>(null);
  /**
   * ผังคนแสดงถึงลำดับไหน
   *
   * ⚠ ค่าตั้งต้นคือ 4 ไม่ใช่ทุกลำดับ — ทั้งหมดถึงซี 9 มี 114 อัตรา
   *   กว้าง 12 คอลัมน์ สูง 17 แถว บนจอจริงต้องย่อเหลือราว 0.16 เท่า
   *   ซึ่งการ์ดเล็กจนอ่านชื่อไม่ออกและเส้นเชื่อมหายไปจากจอ
   *   ลำดับ 1-4 คือ 33 อัตรา ซึ่งอ่านได้ตั้งแต่เปิดมา แล้วกดดูทั้งหมดได้
   *   ห้ามเปลี่ยนค่าตั้งต้นเป็นทุกลำดับโดยไม่แก้ขนาดการ์ดหรือวิธีเล็งกล้องด้วย
   */
  const [peopleTier, setPeopleTier] = useState<4 | 5>(4);
  /**
   * ผังคนจัดแบบไหน — ตามตำแหน่ง (กล่องละอัตรา แยกรายกรมใต้รองปลัด) หรือ ลำดับชั้น (แถบ 1-5)
   *
   * ⚠ ค่าตั้งต้นคือ "ตามตำแหน่ง" ตามที่ผู้ใช้สั่ง (19 ก.ย. 2569) ผังลำดับชั้นเดิมยังอยู่ครบ
   *   สลับได้จากปุ่มบนแถบ — อย่าลบทิ้ง เพราะมันเป็นผังเดียวที่ย่อดูทั้งกระทรวงในจอเดียวได้
   */
  const [peopleLayout, setPeopleLayout] = useState<'positions' | 'tree'>('positions');
  /** แผงรายละเอียดเปิดขวา — เฉพาะผังผู้บริหารตามตำแหน่ง (ผังลำดับชั้นยังเปิดเป็นหน้าต่าง) */
  const seatOpen = view === 'people' && peopleLayout === 'positions' && personSeat !== null;
  const closeSeat = useCallback(() => setPersonSeat(null), []);
  /** กราฟคลื่นเกษียณของทั้งกระทรวง — เปิดจากปุ่มบนผังผู้บริหาร */
  const [peopleWave, setPeopleWave] = useState(false);
  /* ⚠ กราฟคลื่นเกษียณเปิดเป็นหน้าต่างกลางจอ (modal) ไม่ใช่แผงข้างแล้ว — ผู้ใช้สั่ง 19 ก.ย. 2569
       แผงรายละเอียดคนที่เปิดค้างอยู่ไม่ถูกปิด ปิดหน้าต่างกราฟแล้วกลับมาเจอแผงเดิม
       (RetirementWaveModal ยังรองรับ asPanel อยู่ ถ้าวันหน้าจะกลับไปเป็นแผงข้าง) */
  const closeWave = useCallback(() => setPeopleWave(false), []);
  /**
   * แผงเปิดด้านไหนของผังผู้บริหาร — ผู้ใช้เลือกเองได้ (ผู้ใช้สั่ง 19 ก.ย. 2569)
   * จำไว้ในเบราว์เซอร์ของผู้ใช้คนนั้น · ⚠ ห่อ try เสมอ — โหมดส่วนตัวบางตัวอ่าน/เขียน storage ไม่ได้
   */
  const [panelSide, setPanelSide] = useState<'left' | 'right'>(() => {
    try {
      return localStorage.getItem('yana.panelSide') === 'left' ? 'left' : 'right';
    } catch {
      return 'right';
    }
  });
  const flipPanel = useCallback(() => {
    setPanelSide((s) => {
      const next = s === 'left' ? 'right' : 'left';
      try {
        localStorage.setItem('yana.panelSide', next);
      } catch {
        /* จำไม่ได้ก็ไม่เป็นไร — ยังสลับได้ในรอบนี้ */
      }
      return next;
    });
  }, []);
  const sideToggle = (
    <button
      type="button"
      className="icobtn"
      onClick={flipPanel}
      aria-label={panelSide === 'left' ? 'ย้ายแผงไปทางขวา' : 'ย้ายแผงไปทางซ้าย'}
      data-tip={panelSide === 'left' ? 'ย้ายแผงไปขวา' : 'ย้ายแผงไปซ้าย'}
    >
      <SwapOutlined />
    </button>
  );
  /**
   * หน้าต่างบัญชีตำแหน่งที่ปลัดมีอำนาจสั่งบรรจุและแต่งตั้งตามมาตรา 57
   *
   * ⚠ ย้ายทางเข้ามาไว้ในแถบของผังคน เพราะไอคอนบนการ์ดกระทรวงเปลี่ยนไปทำอย่างอื่นแล้ว
   *   ถ้าไม่ย้าย หน้าต่างนี้จะไม่มีทางเข้าเลยทั้งที่โค้ดยังอยู่ครบ
   *   (เป็นอาการเดียวกับที่ทำให้ไอคอนหายไปเงียบ ๆ รอบที่แล้ว — ดู check-screen-parts.ts)
   */
  const [exec, setExec] = useState(false);

  /**
   * ⚠ ไอคอนขึ้นเฉพาะบัญชีมุมมองปลัด ตามที่ผู้ใช้สั่ง
   *
   * ไม่ใช่แค่เรื่องหน้าตา — ผังคนแสดงชื่อคนระดับ 9 ขึ้นไปทั้งกระทรวงเรียงเป็นผัง
   * บัญชีที่มองเห็นได้เฉพาะบางหน่วยจะได้ผังที่ขาดเป็นรู ๆ โดยไม่รู้ว่าขาดเพราะสิทธิ์
   * ซึ่งอ่านได้ว่าตำแหน่งเหล่านั้นไม่มีอยู่จริง
   */
  const canSeePeople = user?.view === 'c9plus';

  /* โหลดเมื่อกดดูเท่านั้น ไม่ใช่โหลดพร้อมหน้าแรก — คนส่วนใหญ่ไม่ได้เปิดผังคน */
  const peopleTree = useQuery({
    queryKey: ['executive-tree'],
    queryFn: () => api.executiveTree(),
    enabled: canSeePeople && view === 'people' && peopleLayout === 'tree',
  });

  /* ผังตามตำแหน่ง — โหลดเมื่อเลือกแบบนี้เท่านั้น */
  const peoplePositions = useQuery({
    queryKey: ['executive-positions'],
    queryFn: () => api.executivePositions(),
    enabled: canSeePeople && view === 'people' && peopleLayout === 'positions',
  });

  /**
   * ยอดของ "สิ่งที่เห็นอยู่ตอนนี้" ไม่ใช่ยอดของทั้งผัง
   *
   * ⚠ ต้องนับตาม peopleTier ที่กำลังเลือก ไม่ใช่หยิบ shown จากเซิร์ฟเวอร์มาตรง ๆ
   *   shown คือยอดของทั้งผัง (ทุกลำดับ) ส่วนหน้าจอตัดเหลือลำดับ 1-4 เป็นค่าตั้งต้น
   *   ถ้าเอา shown มาแสดง ผู้ใช้จะนับการ์ดบนจอแล้วไม่ตรงกับตัวเลขบนแถบ
   */
  const peopleShown = useMemo(() => {
    const root = peopleTree.data?.root;
    if (!root) return null;
    let positions = 0;
    let vacant = 0;
    const walk = (n: ExecutiveTreeNode) => {
      if (n.tier <= peopleTier) {
        positions += 1;
        if (n.vacant) vacant += 1;
      }
      n.children.forEach(walk);
    };
    walk(root);
    return { positions, vacant, occupied: positions - vacant };
  }, [peopleTree.data, peopleTier]);

  const seatQuery = useQuery({
    queryKey: ['position', personSeat],
    queryFn: () => api.position(personSeat!),
    enabled: personSeat !== null,
    /* กดการ์ดใบใหม่ขณะแผงขวาเปิดอยู่ — คงของใบเดิมไว้จนใบใหม่มาถึง แผงจะไม่ปิดแล้วเปิดใหม่กระพริบ */
    placeholderData: (prev) => (personSeat !== null ? prev : undefined),
  });

  /** หน้าต่างคู่คิด (ถาม-ตอบด้วย AI) เปิดอยู่ไหม */
  const [kookit, setKookit] = useState(false);

  /** จอแคบยุบแถบปุ่มเป็นเมนู — เก็บสถานะกางไว้ที่นี่ */
  const narrow = useNarrow(NARROW);
  const [menu, setMenu] = useState(false);
  const menuBox = useRef<HTMLDivElement | null>(null);

  /**
   * ปิดเมนูเมื่อกด Escape หรือคลิกนอกกล่อง
   *
   * ⚠ ใช้ pointerdown ไม่ใช่ click — บน iOS ปุ่มที่อยู่ใต้เมนูจะได้รับ click ต่อทันที
   * ถ้ารอจังหวะ click ผู้ใช้จะปิดเมนูแล้วเผลอกดของที่อยู่ข้างหลังไปด้วยในแตะเดียว
   */
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!menuBox.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [menu]);

  /** กว้างขึ้นแล้วต้องไม่มีเมนูค้างอยู่ ไม่งั้นมันจะลอยทับแถบปุ่มที่เพิ่งกลับมา */
  useEffect(() => {
    if (!narrow) setMenu(false);
  }, [narrow]);

  /**
   * อัตราที่กำลังเปิดดูรายละเอียด
   *
   * เก็บเป็นตัว Position ทั้งก้อน ไม่ใช่ index หรือ posNo
   * เพราะ posNo ซ้ำกันได้ในข้อมูลจริง (ดู posId ใน OrgFlow) และ index จะชี้ผิดตัวทันที
   * ถ้าวันหนึ่งรายการนี้เรียงใหม่หรือถูกกรอง
   */
  const [pos, setPos] = useState<{ p: Position; unitName: string } | null>(null);

  const [legend, setLegend] = useState(() => {
    try {
      return window.matchMedia('(min-width: 900px)').matches;
    } catch {
      return true;
    }
  });

  /** ดัชนี id → หน่วยงาน สร้างครั้งเดียวต่อผังหนึ่งต้น
      ถ้าไล่หาใหม่ทุกครั้งที่กด จะเดินต้นไม้ทั้งต้นต่อหนึ่งคลิกโดยไม่จำเป็น */
  const index = useMemo(() => {
    const map = new Map<string, OrgNode>();
    const walk = (n: OrgNode) => {
      map.set(n.id, n);
      for (const c of n.children ?? []) walk(c);
    };
    for (const r of tree) walk(r);
    return map;
  }, [tree]);

  /**
   * รายการหน่วยงานในช่องเลือกขอบเขต — เฉพาะกลุ่มภารกิจกับส่วนราชการระดับกรม
   *
   * ────────────────────────────────────────────────────────────────
   *  ทำไมไม่กางทั้งหมด
   * ────────────────────────────────────────────────────────────────
   *
   * เดิมใส่ทุกหน่วยทุกชั้นลงไป ซึ่งในชุดข้อมูลนี้คือ 600 กว่ารายการในช่องเลือกเดียว
   * ไม่มีใครไถหาได้ และบนมือถือที่ <select> กลายเป็นตัวเลือกเต็มจอ ยิ่งไถไม่จบ
   *
   * ช่องนี้ทำหน้าที่ "เลือกขอบเขตที่จะดู" ซึ่งเป็นการตัดสินใจระดับกระทรวงกับกรม
   * ส่วนการไล่ลงไปหาสำนักหรือส่วน เป็นงานของผังเอง ที่กดกางทีละกิ่งได้ตรง ๆ
   * และเห็นบริบทรอบข้างไปด้วย — สองอย่างนี้คนละงาน ไม่ควรยัดไว้ในเครื่องมือเดียว
   *
   * ⚠ ตัดสินจาก kind ที่เซิร์ฟเวอร์ส่งมา ห้ามเปลี่ยนไปนับความลึกเอง
   * ความลึกของกรมคือ 2 ก็จริง แต่วันที่ผังเพิ่มชั้นอีกชั้น เลขนั้นจะเลื่อนเงียบ ๆ
   * แล้วช่องเลือกจะกลายเป็นรายชื่อสำนักโดยไม่มีอะไรฟ้อง
   */
  const options = useMemo(() => {
    const out: ScopeOption[] = [];
    const walk = (n: OrgNode, depth: number) => {
      if (n.kind === 'missionGroup' || n.kind === 'department') {
        // ⚠ พา kind/total/vacant ไปด้วย ไม่ใช่แค่ชื่อ
        // แผงที่กางออกมาแสดงกรอบอัตรากำลังกับอัตราว่างในบรรทัดเดียวกับชื่อหน่วย
        // และใช้ kind แยกหัวกลุ่มภารกิจออกจากกรม ห้ามเดาจาก depth
        out.push({ id: n.id, label: n.name, depth, kind: n.kind, total: n.total, vacant: n.vacant });
      }
      // ไล่ลงต่อได้เฉพาะชั้นที่ยังไม่ถึงกรม ลึกกว่านั้นไม่มีอะไรที่ช่องนี้ต้องใช้
      if (n.kind === 'ministry' || n.kind === 'missionGroup') {
        for (const c of n.children ?? []) walk(c, depth + 1);
      }
    };
    for (const r of tree) walk(r, 0);
    return out;
  }, [tree]);

  /**
   * ขอบเขตที่กำลังดูอยู่ต้องมีอยู่ในรายการเสมอ แม้จะลึกกว่าระดับกรม
   *
   * ปุ่ม "ดูเฉพาะสายนี้" ในแผงข้างตั้งขอบเขตเป็นสำนักหรือส่วนได้ ซึ่งไม่อยู่ในรายการ
   * ที่กรองไว้ข้างบน ถ้าไม่เติมกลับเข้าไป ค่าของ <select> จะไม่ตรงกับ option ใดเลย
   * เบราว์เซอร์จะแสดงรายการแรกแทน — ผู้ใช้เห็นชื่อหน่วยที่ตัวเองไม่ได้เลือก
   */
  const scopeOptions = useMemo(() => {
    if (!scope || options.some((o) => o.id === scope)) return options;
    const node = index.get(scope);
    if (!node) return options;
    return [...options, { id: node.id, label: node.name, depth: node.depth, kind: node.kind, total: node.total, vacant: node.vacant }];
  }, [options, scope, index]);

  /**
   * ยอดของทั้งกระทรวง — บรรทัดแรกของช่องเลือกขอบเขตต้องมีตัวเลขเหมือนบรรทัดอื่น
   *
   * ⚠ หยิบจากผัง ไม่ใช่จาก summary ทั้งที่ summary ก็มีตัวเลขชุดนี้
   * เพราะบรรทัดอื่นในรายการมาจากผัง ถ้าบรรทัดแรกมาจากอีกที่ วันที่สองที่นั้นไม่ตรงกัน
   * ผู้ใช้จะเห็นยอดกระทรวงไม่เท่ากับผลรวมของกรมในรายการเดียวกัน โดยไม่มีอะไรอธิบาย
   */
  const ministryCount = useMemo(() => {
    let total = 0;
    let vacant = 0;
    for (const r of tree) {
      total += r.total;
      vacant += r.vacant;
    }
    return { total, vacant };
  }, [tree]);

  const scoped = scope ? (index.get(scope) ?? null) : null;

  /**
   * ตัวเลขบนแถบด้านบนต้องเป็นของขอบเขตที่กำลังดูอยู่ ไม่ใช่ของทั้งกระทรวงเสมอ
   *
   * ⚠ นี่คือจุดที่พังง่ายที่สุดของทั้งหน้า
   * ถ้าปล่อยให้แถบบนบอกยอดทั้งกระทรวงขณะที่ผังบอกยอดของกรมเดียว ผู้ใช้จะเห็นเลขสองชุด
   * บนจอเดียวกันที่ไม่ตรงกันโดยไม่มีคำอธิบาย ซึ่งเป็นจุดที่ทำให้เลิกเชื่อทั้งหน้า
   *
   * ตอนดูทั้งกระทรวงใช้ตัวเลขจากเซิร์ฟเวอร์ตรง ๆ เพราะเป็นตัวตั้งที่กระทบยอดได้
   * ตอนเจาะดูหน่วยเดียวจึงคำนวณจากผังในมือ — ได้ครบทุกช่องยกเว้น "มีงบแล้ว/ยังไม่มีงบ"
   * ซึ่งไม่มีในโครงสร้างผัง จึงสลับไปบอก "ว่างในระดับสำคัญ" แทน ไม่ใช่เดาตัวเลขขึ้นมาเอง
   */
  const stats = useMemo(() => summarize(scoped, summary), [scoped, summary]);

  /**
   * ผังที่ผ่านตัวกรองจากกล่องตัวเลขแล้ว
   *
   * ⚠ กรองหลังจากเลือกขอบเขตแล้ว ไม่ใช่ก่อน
   * ตัวเลขบนแถบด้านบนคำนวณจาก `scoped` ซึ่งเป็นผังก่อนกรอง จึงยังเป็นยอดของทั้งขอบเขต
   * ตามที่ตั้งใจ ถ้ากรองก่อนแล้วค่อยหาขอบเขต ตัวเลขจะกลายเป็นยอดของสิ่งที่เหลือ
   * แล้วช่อง "ว่าง" จะเท่ากับตัวเองเสมอ
   */
  const shownTree = useMemo(
    () => pruneTree(scoped ? [scoped] : tree, effFilter, summary.fiscalYear),
    [scoped, tree, effFilter, summary.fiscalYear],
  );


  /**
   * ตารางหน่วยงานที่จะส่งออก — ตรงกับผังที่เห็นอยู่ ทั้งขอบเขตและตัวกรอง
   *
   * ⚠ ส่งออกเป็น "ตารางหน่วยงาน" ไม่ใช่ "ภาพของผัง"
   *
   * ภาพผังที่ส่งออกไปจะกลายเป็นรูปที่ค้นข้อความไม่ได้ คัดลอกตัวเลขไม่ได้ และซูมแล้วแตก
   * ส่วนสิ่งที่คนเอาไปทำงานต่อจริง ๆ คือตัวเลขรายหน่วย ซึ่งเป็นตารางอยู่แล้วโดยธรรมชาติ
   * (ถ้าวันหนึ่งต้องการรูปผังจริง ๆ ปุ่มเต็มจอ + แคปหน้าจอ ให้ผลที่ดีกว่าการฝังภาพลง PDF)
   *
   * ⚠ ยอดในตารางเป็นยอดรวมหน่วยย่อยทุกชั้น เหมือนที่การ์ดบอก
   * แถวของกระทรวงจึงเท่ากับผลรวมของทุกแถวที่เหลือ ห้ามเอาคอลัมน์ไป SUM ทั้งคอลัมน์
   * ต้องเขียนกำกับไว้ ไม่งั้นคนเปิด Excel มาแล้วลาก SUM จะได้ตัวเลขเกินจริงหลายเท่า
   */
  const buildExport = useCallback((): ExportTable => {
    const rows: Array<Record<string, string | number | null>> = [];
    const walk = (n: OrgNode, path: string[]) => {
      rows.push({
        kind: KIND_TH[n.kind],
        name: n.name,
        parent: path[path.length - 1] ?? '—',
        path: path.join(' > ') || '—',
        total: n.total,
        occupied: n.occupied,
        vacant: n.vacant,
        filled: n.total > 0 ? Math.round((n.occupied / n.total) * 100) : 0,
        retiring: n.retiringIn5Years,
        own: (n.positions ?? []).length,
      });
      for (const c of n.children ?? []) walk(c, [...path, n.name]);
    };
    for (const r of shownTree) walk(r, []);

    const where = scoped ? scoped.name : 'ทั้งกระทรวง';
    const note = effFilter === 'total' ? '' : ` · กรอง: ${FILTER_NOTE[effFilter]}`;

    return {
      title: `กำลังคนรายหน่วยงาน - ${where}`,
      subtitle: `${where}${note} · ${rows.length.toLocaleString('th-TH')} หน่วยงาน`,
      warnings: [
        'คอลัมน์ กรอบอัตรา / มีผู้ครอง / ว่าง / จะเกษียณ เป็นยอดรวมหน่วยย่อยทุกชั้น ' +
          'แถวของหน่วยแม่จึงรวมแถวของหน่วยลูกไว้แล้ว — ห้าม SUM ทั้งคอลัมน์ จะได้ตัวเลขเกินจริง',
      ],
      columns: [
        { key: 'kind', label: 'ชั้น', width: 14 },
        { key: 'name', label: 'หน่วยงาน', width: 38 },
        { key: 'parent', label: 'สังกัด', width: 30 },
        { key: 'total', label: 'กรอบอัตรากำลัง', width: 11, numeric: true },
        { key: 'occupied', label: 'ผู้ครองตำแหน่ง', width: 11, numeric: true },
        { key: 'vacant', label: 'อัตราว่าง', width: 9, numeric: true },
        { key: 'filled', label: 'ครองตำแหน่งแล้ว (%)', width: 12, numeric: true },
        { key: 'retiring', label: 'เกษียณภายใน 5 ปี', width: 14, numeric: true },
        { key: 'own', label: 'อัตราที่สังกัดโดยตรง', width: 15, numeric: true },
        { key: 'path', label: 'สายบังคับบัญชา', width: 60 },
      ],
      rows,
    };
  }, [shownTree, scoped, effFilter]);

  const unit = selected ? (index.get(selected) ?? null) : null;

  // ปิดแผงด้วย Esc — แผงกินพื้นที่ผังไปหนึ่งในสาม ต้องปิดได้เร็วโดยไม่ต้องเล็งปุ่มกากบาท
  useEffect(() => {
    if (!unit) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [unit]);

  /**
   * ขยายเต็มจอด้วย Fullscreen API ของเบราว์เซอร์ ไม่ใช่การทำ overlay เต็มหน้าต่างเอง
   *
   * ต่างกันตรงที่ของจริงจะเอาแถบ URL กับแถบแท็บออกไปด้วย ได้ความสูงคืนอีกราว 90-120px
   * ซึ่งบนโน้ตบุ๊ก 13 นิ้วคือความสูงของการ์ดหน่วยงานอีกเกือบหนึ่งชั้น
   * และเป็นสิ่งที่ผู้ใช้คาดหวังจริง ๆ เวลาเอาผังขึ้นจอโปรเจกเตอร์ในห้องประชุม
   *
   * ⚠ ต้องฟัง fullscreenchange ไม่ใช่จำสถานะไว้เอง เพราะผู้ใช้ออกจากเต็มจอด้วย Esc ได้
   * โดยที่ปุ่มของเราไม่รู้เรื่อง แล้วไอคอนจะค้างผิดสถานะ
   */
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    const sync = () => setIsFull(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFull = useCallback(() => {
    const el = shell.current;
    if (!el) return;
    // เบราว์เซอร์ปฏิเสธได้ เช่นเมื่อไม่ได้มาจากการกดของผู้ใช้จริง หรือถูกนโยบายของ iframe ห้ามไว้
    // ปล่อยให้เงียบพอ เพราะปุ่มยังอยู่ให้กดใหม่ได้ และหน้าที่เหลือใช้งานได้ตามปกติ
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void el.requestFullscreen().catch(() => {});
  }, []);

  return (
    <div
      className={`ocv${unit ? ' has-panel' : ''}${listOf || seatOpen ? ' has-list' : ''}${!listOf && seatOpen && panelSide === 'left' ? ' is-panel-left' : ''}${user ? ' is-app' : ''}`}
      ref={shell}
      style={listW === null ? undefined : ({ '--oc-list-w': `${listW}px` } as CSSProperties)}
    >
      {user && (
        <TopBar
          user={user}
          onLogout={onLogout}
          scope={scope}
          scopeLabel={scoped?.name ?? null}
          options={scopeOptions}
          ministry={ministryCount}
          onScope={(next) => {
            setScope(next);
            // ปิดแผงด้วย เพราะหน่วยที่เปิดค้างอยู่อาจไม่ได้อยู่ในขอบเขตใหม่แล้ว
            setSelected(null);
            // ⚠ ล้างตัวกรองด้วย — ตัวกรองที่ค้างจากขอบเขตเดิมทำให้ผู้ใช้เปิดกรมใหม่มาแล้ว
            // เจอผังโล่ง ๆ โดยไม่รู้ว่าเพราะอะไร ซึ่งอ่านออกได้ว่า "กรมนี้ไม่มีข้อมูล"
            setStatFilter(null);
          }}
        />
      )}

      {/* ---------- แถบตัวเลขสี่ใบ (กรอบอัตรากำลัง · มีผู้ครอง · อัตราว่าง · เกษียณปีงบ) ----------

          เอาออกตามที่ผู้ใช้ขอ — <StatStrip> กับ statFilter ยังอยู่ครบในไฟล์นี้

          ⚠ แถบนั้นเป็นทางเดียวที่ตั้งค่า statFilter ได้ พอเอาออก effFilter จึงเป็น
            'total' ตลอด แปลว่า **ตัวกรองผัง (ว่าง / ใกล้เกษียณ) ไม่มีทางเปิดได้อีก**
            ไม่ได้พังเงียบ ๆ — pruneTree กับ metric ยังทำงานถูกที่ค่า 'total'
            แต่ความสามารถหายไปจริง

          ⚠ ถ้าจะเอาตัวกรองกลับมา มีสองทาง: เอาแถบนี้กลับมา หรือย้ายปุ่มกรอง
            ไปไว้ในแถบเครื่องมือมุมขวาบนของผัง — อย่าเพิ่มที่กดที่การ์ดอีกจุด
            เพราะเป้ากดบนการ์ดเคยถูกเอาออกไปแล้วรอบหนึ่งด้วยเหตุผลว่ากดโดนโดยไม่ตั้งใจ */}

      {/* ---------- แถบของผังคน ----------
          ⚠ ปุ่มกลับต้องอยู่ตรงนี้ ไม่ใช่บนการ์ดกระทรวง
            พอสลับมาเป็นผังคนแล้ว การ์ดกระทรวงหายไปจากจอ ถ้าทางกลับอยู่บนการ์ดนั้น
            ผู้ใช้จะไม่มีทางกลับเลยนอกจากโหลดหน้าใหม่

          ⚠ ทุกอย่างอยู่แถวเดียว เพราะแถบนี้กินความสูงของแคนวาสไปตรง ๆ
            เดิมเป็นสองแถว (~110px) ซึ่งกินพื้นที่ผังไปเกือบหนึ่งในห้าของกรอบที่มี
            ผังคนสูงกว่ากรอบอยู่แล้ว ทุกพิกเซลที่แถบนี้กินคือแถวของคนที่หายไปจากจอ

          ⚠ คำว่า "ไม่ใช่สายการบังคับบัญชา" ต้องยังเห็นด้วยตาเปล่า ห้ามย้ายไปไว้ใน tooltip
            อย่างเดียว ผังคนที่วาดเป็นต้นไม้ถูกอ่านว่าเป็นสายบังคับบัญชาโดยอัตโนมัติ
            ย่อได้แค่ส่วนขยาย ส่วนใจความต้องอยู่บนจอ */}
      {/* ⚠ ผังตามตำแหน่งไม่มีแถบนี้แล้ว (ผู้ใช้สั่งเอาออก 19 ก.ย. 2569) — ตัวกรอง ยอด และปุ่มกาง/ยุบ
            อยู่ในแถบบนของ OrgPositions เอง · แถบยังอยู่เฉพาะผังลำดับชั้น */}
      {view === 'people' && peopleLayout === 'tree' && (
        <div className="ocv-people">
          <button type="button" className="ocv-back" onClick={() => setView('units')}>
            <i aria-hidden="true" />
            ผังหน่วยงาน
          </button>

          <span className="ocv-sep" aria-hidden="true" />

          <b className="ocv-people-tt">ผังผู้บริหาร</b>

          {/* ⚠ ยอดต้องเป็นยอดของ "สิ่งที่เห็นอยู่ตอนนี้" ไม่ใช่ยอดของทั้งผัง
              ตอนดูเฉพาะลำดับ 1-4 ถ้าขึ้น 113 ผู้ใช้จะนับการ์ดบนจอแล้วไม่ตรง
              ⚠ ป้าย "ว่าง" ใช้สีแดงเฉพาะเมื่อมีอัตราว่าง — ว่าง 0 ต้องไม่เป็นสีเตือน */}
          {peopleLayout === 'tree' && peopleShown && (
            <span className="ocv-people-n">
              <b>{peopleShown.positions.toLocaleString('th-TH')}</b> ตำแหน่ง
              <em className={peopleShown.vacant > 0 ? undefined : 'is-zero'}>
                ว่าง {peopleShown.vacant.toLocaleString('th-TH')}
              </em>
            </span>
          )}

          {/* ⚠ ชื่อของแต่ละลำดับมาจากเซิร์ฟเวอร์ ไม่ได้พิมพ์ไว้ที่หน้าจอ
              ลำดับกับตัวจับตำแหน่งอยู่ไฟล์เดียวกัน (services/executiveTree.ts)
              ถ้าพิมพ์ซ้ำที่นี่ วันที่แก้ลำดับแล้วลืมแก้หน้าจอ ป้ายจะบอกคนละเรื่องกับผัง */}
          {peopleLayout === 'tree' && peopleTree.data && (
            <ol className="ocv-tiers">
              {peopleTree.data.tiers
                .filter((t) => t.tier <= peopleTier)
                .map((t) => (
                  <li key={t.tier}>
                    <b>{t.tier}</b>
                    {t.label}
                  </li>
                ))}
            </ol>
          )}

          <span
            className="ocv-people-warn"
            title="จัดลำดับชั้นตามตำแหน่งที่กำหนดไว้ ไม่ใช่สายการบังคับบัญชา — ชุดข้อมูลไม่มีฟิลด์ผู้บังคับบัญชา อธิบดีกรมไม่ได้อยู่ใต้บังคับบัญชาของปลัดกระทรวง"
          >
            ไม่ใช่สายการบังคับบัญชา
          </span>

          {/* ⚠ ปุ่มสลับแบบผังอยู่ชิดขวาเสมอ ปุ่มเลือกลำดับขึ้นเฉพาะผังลำดับชั้น
              เพราะผังตามตำแหน่งแสดงทุกอัตราระดับ 9 ขึ้นไปอยู่แล้ว ไม่มีอะไรให้ตัด */}
          <span className="ocv-seg ocv-tierseg" role="group" aria-label="รูปแบบผังผู้บริหาร">
            <button type="button" aria-pressed={false} onClick={() => setPeopleLayout('positions')}>
              ผังตามตำแหน่ง
            </button>
            <button type="button" aria-pressed={peopleLayout === 'tree'} onClick={() => setPeopleLayout('tree')}>
              ผังลำดับชั้น
            </button>
          </span>

          {peopleLayout === 'tree' && peopleTree.data && (
            <span className="ocv-seg" role="group" aria-label="ระดับที่แสดงในผัง">
              <button type="button" aria-pressed={peopleTier === 4} onClick={() => setPeopleTier(4)}>
                ลำดับ 1-4
              </button>
              <button type="button" aria-pressed={peopleTier === 5} onClick={() => setPeopleTier(5)}>
                ถึงซี 9
              </button>
            </span>
          )}

          <button
            type="button"
            className="ocv-roster"
            onClick={() => setExec(true)}
            title="บัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้งตามมาตรา 57"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect x="3" y="2" width="10" height="12" rx="1.5" />
              <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
            </svg>
            บัญชีอำนาจแต่งตั้ง
          </button>
        </div>
      )}

      {view === 'people' && peopleLayout === 'positions' && peoplePositions.isPending && (
        <p className="state sm">กำลังประกอบผังผู้บริหารตามตำแหน่ง…</p>
      )}
      {view === 'people' && peopleLayout === 'positions' && peoplePositions.isError && (
        <p className="state sm error">{String(peoplePositions.error)}</p>
      )}
      {view === 'people' && peopleLayout === 'tree' && peopleTree.isPending && (
        <p className="state sm">กำลังประกอบผังผู้บริหาร…</p>
      )}
      {view === 'people' && peopleLayout === 'tree' && peopleTree.isError && (
        <p className="state sm error">{String(peopleTree.error)}</p>
      )}

      <div className="ocv-body">
        <div className="ocv-stage">
          {/* key ผูกกับขอบเขตและตัวกรอง — เปลี่ยนอย่างใดอย่างหนึ่งแล้วผังต้องเริ่มใหม่
              ไม่ใช่เอากิ่งที่กางค้างจากชุดก่อนหน้ามาด้วย ซึ่งอาจเป็นกิ่งที่ถูกกรองออกไปแล้ว */}
          <OrgFlow
            key={`${scope ?? '__all__'}|${effFilter}`}
            tree={shownTree}
            mode={mode}
            /* บรรทัดล่างของการ์ดในผังจะบอกตัวเลขของกล่อง KPI ที่กดอยู่
               ส่งค่าที่ผู้ใช้กดจริง ไม่ใช่ effFilter — ตอนไม่ได้กด การ์ดต้องกลับไป
               บอก "ว่าง N อัตรา" ตามปกติ ไม่ใช่ค้างอยู่ที่ตัวกรองก่อนหน้า */
            metric={statFilter ?? 'total'}
            fiscalYear={summary.fiscalYear}
            dir={dir}
            /* ⚠ แผงบัญชีอัตรามาก่อนแผงรายชื่อหน่วย
               กดตัวเลขบนการ์ดใบไหน ผังต้องเหลือเฉพาะเส้นทางลงมาถึงใบนั้น
               (กระทรวง → กลุ่มภารกิจ → กรม → สำนัก) ไม่ใช่กางทั้งกระทรวงค้างไว้
               OrgFlow ใช้ prop นี้ทั้งไฮไลต์การ์ดและตัดกิ่ง (ดู lineageIds กับ ancestorPath)
               จึงส่งหน่วยของแผงที่เปิดอยู่เข้าไปเป็นตัวเดียวกัน ไม่ได้ทำ prop ชุดที่สอง */
            selected={listOf?.id ?? selected}
            onOpenList={(id, mode) => setListOf({ id, mode })}
            /* กดชื่อหน่วยบนการ์ด = เจาะดูเฉพาะสายนั้น
               ⚠ ใช้ setScope ตัวเดียวกับช่องเลือกขอบเขตบนแถบบน ไม่ได้ทำสถานะชุดที่สอง
                 ทางกลับจึงมีอยู่แล้วในช่องนั้น และ key ของผังผูกกับ scope อยู่แล้ว
                 ผังจึงเริ่มใหม่ตอนเปลี่ยนสาย ไม่เอากิ่งที่กางค้างจากสายก่อนหน้ามาด้วย */
            onScope={setScope}
            /* ⚠ ไอคอนบนการ์ดกระทรวงขึ้นเฉพาะมุมมองปลัด — ไม่ส่ง prop = ไม่มีไอคอน
               ไม่ใช่ส่งไปแล้วให้การ์ดซ่อนเอง การตัดสินใจเรื่องสิทธิ์อยู่ที่นี่ที่เดียว */
            onExecutives={canSeePeople ? () => setView('people') : undefined}
            people={view === 'people' ? peopleTree.data ?? null : undefined}
            peopleMaxTier={peopleTier}
            onOpenPosition={(posId) => setPersonSeat(posId)}
            toolsExtra={
              <>
                {/* เดิมมีปุ่มดาวน์โหลด PDF กับ Excel อยู่ตรงนี้ (<ExportButtons build={buildExport} compact />)
                    เอาออกจากหน้าแรกตามที่ผู้ใช้ขอ

                    ⚠ buildExport ยังอยู่ และยังใช้งานได้ครบ — มันประกอบ "ตารางหน่วยงาน"
                      ที่ตรงกับผังที่เห็นอยู่ ทั้งขอบเขตและตัวกรอง ถ้าจะเอาปุ่มกลับมา
                      ใส่บรรทัดข้างบนคืนได้เลย ไม่ต้องประกอบตารางใหม่
                      (ปุ่มส่งออกในหน้าต่างเครื่องมืออีกสี่ตัวไม่ได้ถูกแตะ ยังดาวน์โหลดได้เหมือนเดิม) */}
                <ThemeToggle />
                {/* สลับทิศผัง — ปุ่มเดียวสองสถานะแบบเดียวกับปุ่มเต็มจอข้าง ๆ
                    ไอคอนบอก "ทิศที่จะเปลี่ยนไป" ไม่ใช่ทิศปัจจุบัน เพราะผู้ใช้เห็นทิศปัจจุบัน
                    จากตัวผังอยู่แล้ว สิ่งที่เขาต้องเดาคือกดแล้วจะได้อะไร */}
                <button
                  type="button"
                  className="icobtn"
                  onClick={() => setDir((d) => (d === 'TB' ? 'LR' : 'TB'))}
                  aria-label={dir === 'TB' ? 'จัดผังแนวนอน' : 'จัดผังแนวตั้ง'}
                  data-tip={dir === 'TB' ? 'แนวนอน (ซ้ายไปขวา)' : 'แนวตั้ง (บนลงล่าง)'}
                >
                  {dir === 'TB' ? <ColumnWidthOutlined /> : <ColumnHeightOutlined />}
                </button>
                <button
                  type="button"
                  className="icobtn"
                  onClick={toggleFull}
                  aria-pressed={isFull}
                  aria-label={isFull ? 'ออกจากโหมดเต็มจอ' : 'ขยายเต็มจอ'}
                  data-tip={isFull ? 'ออกจากเต็มจอ (Esc)' : 'เต็มจอ'}
                >
                  {isFull ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                </button>
              </>
            }
          />

          {/* ผังตามตำแหน่ง — ปูทับผืนผ้าใบทั้งกรอบ
              ⚠ ไม่ถอด OrgFlow ออก เพราะมันถือสถานะกล้องและกิ่งที่กางของผังหน่วยงานอยู่
                กดกลับไปผังหน่วยงานแล้วผู้ใช้ต้องเจอผังเดิมที่ค้างไว้ ไม่ใช่ผังที่เริ่มใหม่ */}
          {view === 'people' && peopleLayout === 'positions' && peoplePositions.data && (
            <div className="opv-layer">
              <OrgPositions
                data={peoplePositions.data}
                onOpen={(posId) => setPersonSeat(posId)}
                onRetireWave={() => setPeopleWave(true)}
                onUnits={() => {
                  setPersonSeat(null);
                  setView('units');
                }}
                /* เปิดรายละเอียดคนไหน ผังเหลือเฉพาะสายของคนนั้น — ปิดแผงแล้วกลับมาเต็ม */
                focusId={seatOpen ? personSeat : null}
                /* ชุดเครื่องมือเดียวกับผังหน่วยงาน — ไม่มีปุ่มสลับทิศ เพราะผังนี้วาดแนวตั้งอย่างเดียว */
                toolsExtra={
                  <>
                    <ThemeToggle />
                    <button
                      type="button"
                      className="icobtn"
                      onClick={toggleFull}
                      aria-pressed={isFull}
                      aria-label={isFull ? 'ออกจากโหมดเต็มจอ' : 'ขยายเต็มจอ'}
                      data-tip={isFull ? 'ออกจากเต็มจอ (Esc)' : 'เต็มจอ'}
                    >
                      {isFull ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    </button>
                  </>
                }
              />
            </div>
          )}

          {/*
            ปุ่มโหมดลอยอยู่บนแคนวาสมุมซ้ายบน ไม่ใช่แถวของตัวเองเหนือผัง
            คืนความสูงให้ผังไป 80px และเอาปุ่มไปอยู่ใกล้ของที่มันเปลี่ยน คือตัวผังเอง
            มุมซ้ายบนของแคนวาสว่างเสมอ เพราะ fitView จัดผังให้อยู่กลางกรอบ
          */}
          <div className="ocv-modebar" ref={menuBox}>
            {/* ปุ่มกลับไปผังผู้บริหาร (บัญชีปลัด) — ผู้ใช้ขอ 19 ก.ย. 2569 ไอคอนเดียวกับบนการ์ดกระทรวง
                แต่อยู่มุมซ้ายบนให้หาเจอเสมอ ไม่ต้องเลื่อนหาการ์ดกระทรวง */}
            {canSeePeople && view === 'units' && (
              <div className="ocv-execbar">
                <button
                  type="button"
                  className="icobtn ocv-toexec"
                  onClick={() => setView('people')}
                  aria-label="ดูผังผู้บริหาร"
                  data-tip="ผังผู้บริหาร"
                >
                  <TeamOutlined aria-hidden="true" />
                </button>
                {/* กราฟคลื่นเกษียณของทั้งกระทรวง — ปุ่มเดียวกับในผังผู้บริหาร เปิดหน้าต่างกลางจอตัวเดียวกัน (ผู้ใช้ขอ 19 ก.ย. 2569) */}
                <button
                  type="button"
                  className="icobtn"
                  aria-haspopup="dialog"
                  onClick={() => setPeopleWave(true)}
                  aria-label="ดูกราฟคลื่นเกษียณของทั้งกระทรวง"
                  data-tip="คลื่นเกษียณ 5 ปี"
                >
                  <LineChartOutlined aria-hidden="true" />
                </button>
              </div>
            )}
            {narrow ? (
              /* ------------------------------------------------------------------
                 จอแคบ — ปุ่มเจ็ดใบยุบเหลือใบเดียว

                 ของเดิมให้เลื่อนแนวนอน ซึ่งกินความสูงเท่าเดิมและกินความกว้างเกือบทั้งแถว
                 ทับผังไปหนึ่งแถบเต็ม ๆ ทั้งที่ผู้ใช้กดมันครั้งเดียวแล้วดูผังยาว ๆ

                 ⚠ ปุ่มแสดงไอคอนอย่างเดียวตามที่ผู้ใช้ขอ ไม่มีชื่อโหมดติดไปด้วย
                 โหมดที่ใช้อยู่จึงต้องหาได้จากสองทางที่เหลือ — aria-label ของปุ่ม
                 และรายการที่ไฮไลต์ไว้ในเมนูตอนกางออก ห้ามเอาสองอย่างนั้นออกตามไปด้วย
                 ------------------------------------------------------------------ */
              <>
                <button
                  type="button"
                  className="ocv-burger"
                  aria-expanded={menu}
                  aria-haspopup="menu"
                  /* ⚠ aria-label เป็นที่เดียวที่เหลือซึ่งบอกโหมดปัจจุบันเป็นข้อความ
                     ปุ่มมีแต่ไอคอน ถ้าไม่มี label ผู้ใช้ screen reader จะได้ยินแค่ "ปุ่ม" เปล่า ๆ */
                  /* เดิมต่อท้ายด้วยชื่อโหมดปัจจุบัน ตอนนี้ไม่มีปุ่มโหมดแล้วจึงตัดออก
                     ถ้าเพิ่มโหมดกลับเข้า MODES ต้องเอาชื่อโหมดกลับมาต่อท้ายด้วย
                     ไม่งั้นผู้ใช้ screen reader จะไม่มีทางรู้ว่าตอนนี้ผังอยู่โหมดไหน */
                  aria-label="เปิดเมนูเครื่องมือ"
                  onClick={() => setMenu((v) => !v)}
                >
                  <MenuOutlined aria-hidden="true" />
                </button>

                {menu && (
                  <div className="ocv-menu" role="menu" aria-label="เลือกมุมมองและเครื่องมือ">
                    {MODES.length > 0 && <p className="hd">มุมมองของผัง</p>}
                    {MODES.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        role="menuitemradio"
                        aria-checked={mode === m.id}
                        onClick={() => {
                          setMode(m.id);
                          setMenu(false);
                        }}
                      >
                        <b>{m.label}</b>
                        {/* คำอธิบายโหมดย้ายมาอยู่ในเมนูเลย — บนมือถือไม่มี hover ให้แสดง tooltip
                            ถ้าตัดทิ้ง ผู้ใช้มือถือจะไม่มีทางรู้ว่าตัวเลขที่เห็นคำนวณจากอะไร */}
                        <span>{m.hint}</span>
                      </button>
                    ))}

                    {/* ⚠ หัวข้อ "เครื่องมือ" กับปุ่ม "การย้ายและสับเปลี่ยน" ถูกเอาออกตามที่ผู้ใช้ขอ
                        RotationModal กับตัวสถานะ rot ยังอยู่ครบ เอากลับมาได้ด้วยการใส่ปุ่มคืน
                        (แนวเดียวกับ "บัญชีผู้เกษียณอายุราชการ" และ "ผู้มีอำนาจสั่งบรรจุ" ที่เอาออกไปก่อนหน้า) */}
                    {/* เอา "บัญชีผู้เกษียณอายุราชการ" กับ "ผู้มีอำนาจสั่งบรรจุ" ออกแล้ว
                        ต้องตรงกับแถบของจอกว้างเสมอ ดูหมายเหตุที่นั่น */}
                  </div>
                )}
              </>
            ) : (
              /*
                ⚠ อ้าง role="tablist" เฉพาะตอนมีแท็บจริง
                  tablist ที่ไม่มี tab สักใบเป็นโครงที่ผิด และโปรแกรมอ่านหน้าจอ
                  จะประกาศว่ามีชุดแท็บให้เลือกทั้งที่ไม่มีอะไรให้เลือก
                  ตอนนี้ในแถวเหลือแต่ปุ่มเครื่องมือ ซึ่งไม่ใช่แท็บ
              */
              <div
                className="ocv-moderow"
                role={MODES.length > 0 ? 'tablist' : undefined}
                aria-label={MODES.length > 0 ? 'เลือกคำถามที่จะดูกับผัง' : undefined}
              >
                {MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    className="ocv-mode"
                    aria-selected={mode === m.id}
                    onClick={() => setMode(m.id)}
                    /* ⚠ คำอธิบายว่าตัวเลขบนการ์ดหมายถึงอะไร ย้ายมาอยู่ในนี้ตามที่ผู้ใช้ขอให้เอาบรรทัดล่างออก
                       ห้ามตัดข้อความนี้ทิ้งไปเฉย ๆ — ชื่อปุ่มสั้น ๆ อย่าง "ผลกระทบ" บอกไม่ได้เลยว่า
                       ตัวเลขที่เห็นคำนวณจากอะไร ซึ่งเป็นสิ่งที่ต้องรู้ก่อนเอาตัวเลขไปใช้
                       (จอแคบข้อความนี้ไปอยู่ในเมนูแทน เพราะมือถือไม่มี hover) */
                    data-tip={m.hint}
                  >
                    {m.label}
                  </button>
                ))}

                {/* ปุ่มถัดจากนี้ไม่ใช่โหมดของผัง มันเปิดหน้าต่างของตัวเอง
                    ไม่ได้เปลี่ยนสิ่งที่ผังกำลังแสดง จึงไม่อยู่ใน role="tablist"

                    เส้นขอบใช้สีเดียวกับปุ่มไอคอนบนขวาแล้ว ไม่ใช่สีทอง — สีทองในระบบนี้
                    แปลว่า "ของกระทรวง" หรือ "กำลังเลือกอยู่" ปุ่มนี้ไม่ได้อยู่ในสองสถานะนั้น
                    (ดูเหตุผลเต็มที่ .ocv-sim ใน styles.css)

                    ⚠ ตอนนี้เหลือปุ่มเดียว และไม่มีปุ่มโหมดมาคั่นหน้ามันแล้ว
                      ช่องไฟที่เคยแยก "กลุ่มโหมด" ออกจาก "กลุ่มเครื่องมือ" จึงไม่ได้ทำหน้าที่อะไร
                      ถ้าเหลือปุ่มเดียวถาวร ควรพิจารณาว่ายังต้องมีแถบนี้อยู่ไหม */}
                {/* ⚠ ปุ่ม "การย้ายและสับเปลี่ยน" (.ocv-sim) ถูกเอาออกตามที่ผู้ใช้ขอ
                    ต้องตรงกับเมนูของจอแคบเสมอ ถ้าเอากลับ ต้องใส่คืนทั้งสองที่ */}

                {/* เดิมมีอีกสองปุ่มต่อท้ายตรงนี้ — "บัญชีผู้เกษียณอายุราชการ" (setRet)
                    กับ "ผู้มีอำนาจสั่งบรรจุ" (setAuth) เอาออกตามที่ผู้ใช้ขอ

                    ⚠ หน้าต่างทั้งสองยังอยู่ครบในไฟล์นี้ (RetirementRosterModal · AuthorityModal)
                      พร้อมตัวสถานะของมัน แค่ไม่มีปุ่มไหนเปิดได้แล้ว
                      ตั้งใจไม่ลบ เพื่อให้การเอากลับมาเป็นการใส่ปุ่มคืน ไม่ใช่เขียนใหม่
                      ถ้าจะเอากลับ ต้องใส่คืนทั้งที่นี่และในเมนูของจอแคบข้างบน */}
              </div>
            )}
          </div>
          {/*
            เดิมมีแถบ "กรอง: … · แสดง N จาก M หน่วยงาน [ล้างตัวกรอง]" อยู่ตรงนี้

            มันมีไว้กันไม่ให้ผังเล็กลงเงียบ ๆ — ผู้ใช้ที่เผลอกดกล่องตัวเลขแล้วเห็น
            หน่วยงานหายไปครึ่งผัง จะอ่านออกว่า "ระบบโหลดข้อมูลไม่ครบ" ไม่ใช่
            "ฉันกดตัวกรองไว้"

            ตอนนี้กล่องตัวเลขที่กดค้างอยู่บอกเรื่องนี้แทนแล้ว — พื้นทอง กรอบทอง
            ไอคอนกรวยสีเข้ม และ aria-pressed ซึ่งเห็นตลอดเวลาที่กรองอยู่
            กดใบเดิมซ้ำก็เลิกกรองได้ ไม่ต้องมีปุ่มล้างแยก

            ⚠ ถ้าวันหนึ่งกล่องตัวเลขเลิกแสดงสถานะกดค้าง ต้องเอาแถบนี้กลับมา
              ไม่งั้นจะไม่มีอะไรบนจอบอกว่าผังถูกกรองอยู่เลย
          */}
          {/* ⚠ แถบคำอธิบายสีมุมล่างซ้ายถูกเอาออกตามที่ผู้ใช้ขอ
              คอมโพเนนต์ Legend กับตัวสถานะ legend ยังอยู่ครบในไฟล์นี้ */}
        </div>

        {/* บัญชีอัตราของหน่วยงาน เปิดจากปุ่มตัวเลขบนการ์ด
            ⚠ เป็นคอลัมน์ "ขวา" ของ .ocv-body ไม่ใช่หน้าต่างซ้อน — ผังฝั่งซ้ายจึงยังกดได้
              และยอดบนการ์ดยังเห็นพร้อมกับรายการที่กดเข้ามาดู

            ⚠ ลำดับใน DOM คือลำดับของคอลัมน์ — ผังต้องมาก่อนแผงนี้เสมอ
              เดิมแผงนี้อยู่ซ้าย ผู้ใช้สั่งให้สลับข้าง การสลับทำที่ลำดับใน DOM
              ไม่ใช่ที่ order ของกริด เพราะลำดับการไล่ด้วยแป้น Tab กับลำดับที่
              โปรแกรมอ่านหน้าจออ่าน ต้องตรงกับสิ่งที่ตาเห็น
              (grid-template-columns ใน styles.css ต้องเรียงตามลำดับนี้ด้วย)

            ชื่อหน่วยเอามาจาก index ไม่ใช่จาก id เพราะ id เป็นเส้นทางเต็มซึ่งยาวเกินไปสำหรับหัวแผง */}
        {/* การ์ดคนในผังผู้บริหาร → แผงรายละเอียดเปิดขวา ช่องเดียวกับแผงบัญชีอัตรา (ผู้ใช้สั่ง 19 ก.ย. 2569)
            ⚠ รอโหลดเสร็จก่อนถึงเปิด — แผงที่เปิดมาว่างแล้วค่อยมีของ อ่านได้ว่ากดผิดใบ */}
        {!listOf && seatOpen && seatQuery.data && (
          <PositionSidePanel
            p={seatQuery.data}
            width={listW}
            onClose={closeSeat}
            sideToggle={sideToggle}
            grip={<PanelGrip side={panelSide} onResize={setListW} />}
          />
        )}
        {listOf && (
          <PositionListPanel
            unitPath={listOf.id}
            unitName={index.get(listOf.id)?.name ?? listOf.id}
            mode={listOf.mode}
            width={listW}
            onResize={setListW}
            onClose={() => setListOf(null)}
          />
        )}

        {unit && (
          <UnitDrawer
            unit={unit}
            mode={mode}
            /* ⚠ ส่ง effFilter ไม่ใช่ statFilter — ตอนยังไม่ได้กดกล่องไหน effFilter เป็น 'total'
                 ซึ่ง positionMatches ปล่อยผ่านทุกอัตราอยู่แล้ว แผงจึงไม่ต้องรู้จักค่า null */
            filter={effFilter}
            fiscalYear={summary.fiscalYear}
            onClose={() => setSelected(null)}
            onOpenPosition={(p) => setPos({ p, unitName: unit.name })}
          />
        )}
      </div>

      {/* หน้าต่างทั้งสองเป็นพี่น้องกับแถบบน จึงทับแถบบนได้จริงตาม z-index ที่ตั้งไว้ */}
      {pos && <PositionModal position={pos.p} unitName={pos.unitName} onClose={() => setPos(null)} />}

      {/* กดการ์ดคนในผังคน → เปิดหน้าต่างรายละเอียดอัตราตัวเดิม ไม่ได้ทำหน้าต่างชุดที่สอง
          ⚠ ต้องรอโหลดก่อนถึงเปิด ไม่ใช่เปิดหน้าต่างเปล่ารอ — หน้าต่างที่เปิดมาว่าง ๆ
            แล้วค่อยมีของ อ่านได้ว่ากดผิดใบ แล้วผู้ใช้จะกดปิดก่อนข้อมูลมาถึง */}
      {/* ผังลำดับชั้น (OrgPeople) ยังเปิดเป็นหน้าต่างเหมือนเดิม — ผังตามตำแหน่งเปิดเป็นแผงขวาแทน */}
      {personSeat && !seatOpen && seatQuery.data && (
        <PositionModal
          position={seatQuery.data}
          unitName={seatQuery.data.orgPath[seatQuery.data.orgPath.length - 1] ?? seatQuery.data.unit}
          onClose={() => setPersonSeat(null)}
        />
      )}

      {/* ⚠ รหัสโหนดของกระทรวงมาจากผังจริง ไม่ได้พิมพ์ชื่อเอง — เซิร์ฟเวอร์กรองตามบัญชีปลัด (ระดับ 9 ขึ้นไป) ให้อยู่แล้ว */}

      {/* กราฟคลื่นเกษียณของทั้งกระทรวง — หน้าต่างกลางจอ (ผู้ใช้สั่ง 19 ก.ย. 2569)
          ⚠ รหัสโหนดของกระทรวงมาจากผังจริง ไม่ได้พิมพ์ชื่อเอง */}
      {canSeePeople && peopleWave && (
        <RetirementWaveModal
          unitPath={tree.find((n) => n.kind === 'ministry')?.id ?? tree[0]?.id ?? 'กระทรวงการคลัง'}
          unitName={tree.find((n) => n.kind === 'ministry')?.name ?? 'กระทรวงการคลัง'}
          onClose={closeWave}
        />
      )}

      {sim && <RotationModal onClose={() => setSim(false)} />}
      {rot && <RotationPlanModal onClose={() => setRot(false)} />}
      {ret && <RetirementRosterModal onClose={() => setRet(false)} />}
      {auth && <AuthorityModal onClose={() => setAuth(false)} />}
      {exec && <ExecutiveRosterModal onClose={() => setExec(false)} />}
      {/*
        ⚠ ปุ่มลอยอยู่นอก .ocv-body และไม่ได้อยู่ในแถบเครื่องมือ

        ย้ายมาจากแถบด้านบนตามที่ผู้ใช้ขอ ซึ่งช่วยเรื่องที่ทักไว้ก่อนหน้าด้วย —
        แถบนั้นแน่นจนถูกทักมาสองรอบ ตอนนี้กลับไปเหลือหกปุ่มเท่าเดิม

        ซ่อนตอนหน้าต่างเปิดอยู่ ไม่ใช่ปล่อยให้ทับกัน เพราะปุ่มที่กดแล้วเปิดสิ่งที่เปิดอยู่แล้ว
        ไม่ได้ทำอะไร แต่กินพื้นที่และดึงสายตา
      */}
      <KookitLauncher hidden={kookit} onOpen={() => setKookit(true)} />
      {kookit && <KookitModal onClose={() => setKookit(false)} />}

      {user && <FootBar />}

    </div>
  );
}

/* ---------------- แถบบนสุด ---------------- */

function TopBar({
  user,
  onLogout,
  scope,
  scopeLabel,
  options,
  ministry,
  onScope,
}: {
  user: SessionUser;
  onLogout?: () => void;
  scope: string | null;
  scopeLabel: string | null;
  options: ScopeOption[];
  ministry: { total: number; vacant: number };
  onScope: (next: string | null) => void;
}) {
  return (
    <header className="ocv-top">
      <div className="brand">
        {/* ตราสัญลักษณ์ย่อ — ลายข้าวหลามตัดซ้อนสองชั้น ถอดมาจากลายอัตลักษณ์ของกระทรวง
            เป็นรูปประดับล้วน คนที่ใช้โปรแกรมอ่านหน้าจอได้ชื่อระบบจากตัวหนังสือข้าง ๆ อยู่แล้ว */}
        <span className="dot" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M12 2.5 21.5 12 12 21.5 2.5 12Z" />
            <path d="M12 7.2 16.8 12 12 16.8 7.2 12Z" />
          </svg>
        </span>
        <span className="txt">
          <b>YANA</b>
          <em>ระบบวิเคราะห์และวางแผนอัตรากำลัง · สำนักงานปลัดกระทรวงการคลัง</em>
        </span>
      </div>

      <ScopePicker
        scope={scope}
        scopeLabel={scopeLabel}
        options={options}
        ministry={ministry}
        onScope={onScope}
      />

      {/* ป้าย "มุมมองปลัดกระทรวง · เห็นเฉพาะตำแหน่งระดับ 9 ขึ้นไป" เอาออกตามที่ผู้ใช้สั่ง (19 ก.ย. 2569)
          ⚠ เดิมตั้งใจให้ขึ้นเสมอ เพราะยอดทุกตัวถูกตัดตั้งแต่ฝั่งเซิร์ฟเวอร์
            ตอนนี้ขอบเขตยังบอกอยู่ที่ช่อง "ตำแหน่งระดับ 9 ขึ้นไป" บนผังผู้บริหาร
            ถ้าบัญชีมุมมองนี้ใช้หน้าอื่นที่ไม่มีคำบอกขอบเขต ต้องหาที่บอกใหม่ ห้ามให้ยอดถูกกรองเงียบ ๆ
            (user.viewLabel / viewNote ยังส่งมาจากเซิร์ฟเวอร์ครบ เอากลับมาได้ทันที) */}

      <div className="who">
        <span className="nm">{user.displayName}</span>
        {user.role && <span className="rl">{user.role}</span>}
      </div>

      <button type="button" className="out" onClick={onLogout}>
        ออกจากระบบ
      </button>
    </header>
  );
}

/* ================================================================
   ช่องเลือกขอบเขต
   ================================================================ */

/** หนึ่งรายการที่เลือกเป็นขอบเขตได้ — สร้างจากผังในหน้า ไม่ใช่จากเซิร์ฟเวอร์แยกอีกชุด */
export interface ScopeOption {
  id: string;
  label: string;
  depth: number;
  /** ⚠ ใช้ตัดสินหน้าตาและการจัดกลุ่ม ห้ามเดาจาก depth (ดูหมายเหตุที่ OrgNodeKind) */
  kind: OrgNode['kind'];
  total: number;
  vacant: number;
}

/** หนึ่งแถวในแผงที่กางออกมา — รวมบรรทัด "ทั้งกระทรวง" ซึ่งไม่มีหน่วยงานรองรับ */
interface ScopeRow {
  /** null = ภาพรวมระดับกระทรวง ตรงกับค่า scope ที่ส่งขึ้นไป */
  id: string | null;
  label: string;
  total: number;
  vacant: number;
  tier: 'all' | 'group' | 'dept' | 'deep';
}

/** กลุ่มภารกิจหนึ่งกลุ่มพร้อมกรมในสังกัด — head เป็น null เมื่อกรมนั้นไม่ได้อยู่ใต้กลุ่มใด */
interface ScopeGroup {
  head: ScopeRow | null;
  rows: ScopeRow[];
}

const ALL_SCOPE_LABEL = 'ภาพรวมระดับกระทรวง';

/**
 * ช่องเลือกขอบเขต — จะดูทั้งกระทรวง หรือเจาะดูหน่วยเดียว
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมเลิกใช้ <select> ของเบราว์เซอร์
 * ────────────────────────────────────────────────────────────────
 *
 * เดิมเป็น <select> โปร่งใสวางทับเปลือกที่จัดหน้าตาเอง ซึ่งได้พฤติกรรมแป้นพิมพ์
 * มาฟรีทั้งหมด แลกกับข้อจำกัดข้อเดียวที่ใหญ่เกินกว่าจะรับไหว —
 * รายการที่กางออกมาเป็นของระบบปฏิบัติการ จัดรูปแบบอะไรไม่ได้เลยนอกจากสีพื้นกับสีตัวอักษร
 *
 * ผลคือสองอย่างที่แก้ไม่ได้ในกรอบเดิม
 *   ๑ ลำดับชั้น "กลุ่มภารกิจ → กรม" ต้องแกล้งด้วยช่องว่างไม่ตัดบรรทัดนำหน้า
 *     ซึ่งกวาดตาแล้วแยกไม่ออกว่าอะไรเป็นกลุ่ม อะไรเป็นกรม
 *   ๒ ไม่มีที่ให้ใส่ตัวเลข — ผู้ใช้ต้องเลือกกรมเข้าไปก่อนถึงจะรู้ว่ากรมนั้นมีกี่อัตรา
 *     แล้วถอยออกมาเลือกใหม่ถ้าไม่ใช่กรมที่ต้องการ
 *
 * แผงที่เขียนเองจึงแลกกลับกัน: ต้องดูแลแป้นพิมพ์และโฟกัสเอง (ทำไว้ครบข้างล่าง)
 * แต่ได้หัวกลุ่มภารกิจที่เห็นชัด กรอบอัตรากำลังกับอัตราว่างในบรรทัดเดียวกับชื่อ
 * และช่องค้นหาที่พิมพ์ชื่อกรมได้ตรง ๆ
 *
 * ⚠ สิ่งที่หายไปพร้อม <select> คือตัวเลือกเต็มจอของระบบบนมือถือ
 *   จอแคบกว่า 860px จึงเปลี่ยนเป็นแผ่นเลื่อนขึ้นจากด้านล่างแทน (ดู .ocv-scope-panel ใน styles.css)
 *   ถ้าเอา media query นั้นออก แผงจะกลายเป็นกล่องลอยกว้าง 452px บนจอ 390px
 */
function ScopePicker({
  scope,
  scopeLabel,
  options,
  ministry,
  onScope,
}: {
  scope: string | null;
  scopeLabel: string | null;
  options: ScopeOption[];
  /** ยอดของทั้งกระทรวง ใช้เติมบรรทัดแรกของรายการ ซึ่งไม่มีหน่วยงานให้หยิบตัวเลขมา */
  ministry: { total: number; vacant: number };
  onScope: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** แถวที่ลูกศรชี้อยู่ — เป็นดัชนีใน flat ไม่ใช่ใน options */
  const [active, setActive] = useState(0);

  const box = useRef<HTMLDivElement | null>(null);
  const field = useRef<HTMLInputElement | null>(null);
  const listBox = useRef<HTMLDivElement | null>(null);

  /**
   * จับรายการแบน ๆ ให้เป็นกลุ่มภารกิจ
   *
   * อาศัยลำดับที่เซิร์ฟเวอร์จัดมาแล้วตามกฎกระทรวงว่าด้วยกลุ่มภารกิจ พ.ศ. 2545
   * คือกลุ่มมาก่อน แล้วกรมในสังกัดตามหลังจนกว่าจะเจอกลุ่มถัดไป
   * ⚠ ห้ามเรียงใหม่ตรงนี้ ลำดับตามกฎกระทรวงเป็นของที่ผู้ใช้ขอไว้ทั้งระบบ
   */
  const groups = useMemo(() => {
    const out: ScopeGroup[] = [];
    let cur: ScopeGroup | null = null;
    for (const o of options) {
      if (o.kind === 'missionGroup') {
        cur = { head: { id: o.id, label: o.label, total: o.total, vacant: o.vacant, tier: 'group' }, rows: [] };
        out.push(cur);
        continue;
      }
      if (!cur) {
        // กรมที่ไม่ได้อยู่ใต้กลุ่มใด — ไม่ควรเกิดกับข้อมูลชุดนี้ แต่ถ้าเกิดต้องยังเห็น
        cur = { head: null, rows: [] };
        out.push(cur);
      }
      cur.rows.push({
        id: o.id,
        label: o.label,
        total: o.total,
        vacant: o.vacant,
        tier: o.kind === 'department' ? 'dept' : 'deep',
      });
    }
    return out;
  }, [options]);

  /**
   * กรองตามคำค้น
   *
   * หัวกลุ่มตรงคำค้น → เอาทั้งกลุ่มมา (คนพิมพ์ "รายได้" ต้องการเห็นกรมในกลุ่มนั้น)
   * ไม่ตรง → เอาเฉพาะกรมที่ตรง แต่ยังโชว์หัวกลุ่มไว้เป็นบริบทว่ากรมนั้นอยู่กลุ่มไหน
   */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    const out: ScopeGroup[] = [];
    for (const g of groups) {
      if (g.head && g.head.label.toLowerCase().includes(q)) {
        out.push(g);
        continue;
      }
      const rows = g.rows.filter((r) => r.label.toLowerCase().includes(q));
      if (rows.length > 0) out.push({ head: g.head, rows });
    }
    return out;
  }, [groups, query]);

  /** รายการเดียวกับที่ตาเห็น เรียงตามลำดับบนลงล่าง — ลูกศรขึ้น-ลงเดินบนตัวนี้ */
  const flat = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out: ScopeRow[] = [];
    if (!q || ALL_SCOPE_LABEL.toLowerCase().includes(q)) {
      out.push({ id: null, label: ALL_SCOPE_LABEL, total: ministry.total, vacant: ministry.vacant, tier: 'all' });
    }
    for (const g of shown) {
      if (g.head) out.push(g.head);
      out.push(...g.rows);
    }
    return out;
  }, [shown, query, ministry]);

  /* ตอนเปิด ให้ลูกศรเริ่มที่แถวที่เลือกอยู่ ไม่ใช่แถวแรกเสมอ
     คนที่เปิดมาเพื่อเปลี่ยนจากกรมหนึ่งไปกรมข้างเคียง จะได้กดลูกศรทีเดียวถึง */
  useEffect(() => {
    if (!open) {
      // ล้างคำค้นตอนปิด ไม่ใช่ตอนเปิด — เปิดครั้งหน้าจะได้เห็นรายการเต็มตั้งแต่แรก
      // และตอนเปิด flat จึงเป็นรายการที่ไม่ถูกกรองแล้วเสมอ ทำให้หาแถวที่เลือกอยู่เจอ
      setQuery('');
      return undefined;
    }
    const at = flat.findIndex((r) => r.id === scope);
    setActive(at >= 0 ? at : 0);
    // โฟกัสช่องค้นหาหลังแผงถูกวาดแล้ว ไม่งั้น ref ยังเป็น null
    const t = window.setTimeout(() => field.current?.focus(), 0);
    return () => window.clearTimeout(t);
    // ตั้งใจไม่ใส่ flat ในรายการพึ่งพา — ต้องทำครั้งเดียวตอนเปิด ไม่ใช่ทุกครั้งที่พิมพ์
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope]);

  /**
   * พิมพ์แล้วรายการเปลี่ยน ตัวชี้ต้องย้ายตาม ไม่ใช่ค้างที่ดัชนีเดิม
   *
   * ⚠ ต้องไปที่ "แถวแรกที่ตรงคำค้นจริง" ไม่ใช่แถวแรกของรายการ
   * เพราะหัวกลุ่มภารกิจถูกโชว์ไว้เป็นบริบทให้รู้ว่ากรมที่เจออยู่กลุ่มไหน
   * ทั้งที่ตัวมันเองไม่ได้ตรงคำค้น — คนที่พิมพ์ "บัญชี" แล้วกด Enter ทันที
   * จะได้ทั้งกลุ่มภารกิจด้านรายจ่ายฯ แทนที่จะได้กรมบัญชีกลางที่เขาพิมพ์หา
   */
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setActive((a) => (a < flat.length ? a : 0));
      return;
    }
    const at = flat.findIndex((r) => r.label.toLowerCase().includes(q));
    setActive(at >= 0 ? at : 0);
  }, [flat, query]);

  /* ปิดเมื่อกด Escape หรือคลิกนอกกล่อง — pointerdown ด้วยเหตุผลเดียวกับเมนูเครื่องมือ */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  /* เลื่อนรายการตามตัวชี้ ไม่งั้นกดลูกศรลงจนพ้นขอบแล้วไม่เห็นว่าชี้อยู่ตรงไหน
     block: 'nearest' เพื่อไม่ให้กระโดดกลางจอทุกครั้งที่ขยับทีละแถว */
  useEffect(() => {
    if (!open) return;
    listBox.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (id: string | null) => {
    onScope(id);
    setOpen(false);
  };

  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((a) => (a + step + flat.length) % flat.length);
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setActive(e.key === 'Home' ? 0 : flat.length - 1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[active];
      if (row) pick(row.id);
      return;
    }
    /* Tab ต้องปิดแผง ไม่ใช่ปล่อยให้โฟกัสหลุดออกไปหลังแผงที่ยังกางค้างอยู่ */
    if (e.key === 'Tab') setOpen(false);
  };

  const label = scope === null ? ALL_SCOPE_LABEL : (scopeLabel ?? ALL_SCOPE_LABEL);
  const here = scope === null ? ministry.total : (options.find((o) => o.id === scope)?.total ?? null);
  const rowId = (i: number) => `ocv-scope-row-${i}`;

  /** วาดหนึ่งแถว — ทุกแถวกดเลือกได้จริง รวมถึงหัวกลุ่มภารกิจ (เลือกกลุ่ม = ดูทั้งกลุ่ม) */
  const drawRow = (row: ScopeRow, i: number) => {
    const on = row.id === scope;
    return (
      <div
        key={row.id ?? '__all__'}
        id={rowId(i)}
        role="option"
        aria-selected={on}
        data-active={i === active}
        className={`rw t-${row.tier}${on ? ' is-on' : ''}`}
        onPointerEnter={() => setActive(i)}
        onClick={() => pick(row.id)}
      >
        <span className="nm">
          {/* ช่องเครื่องหมายถูกกว้างคงที่และมีอยู่ทุกแถว แม้แถวที่ไม่ได้เลือก
              ถ้าใส่เฉพาะแถวที่เลือก ชื่อหน่วยทั้งรายการจะเยื้องไม่ตรงกันหนึ่งแถว */}
          <span className="tk" aria-hidden="true">
            {on && (
              <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m3 8.4 3.4 3.3L13 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="tx">{row.label}</span>
        </span>
        <span className="fg">
          <b>{num(row.total)}</b>
          {row.vacant > 0 && <em>ว่าง {num(row.vacant)}</em>}
        </span>
      </div>
    );
  };

  return (
    <div className={`ocv-scope${open ? ' is-open' : ''}`} ref={box}>
      <button
        type="button"
        className="pill"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`ขอบเขตที่กำลังดู: ${label} — กดเพื่อเปลี่ยน`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ic" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M8 1.8 14.5 5 8 8.2 1.5 5 8 1.8Z" strokeLinejoin="round" />
            <path d="M2.4 8.2 8 11l5.6-2.8M2.4 11.2 8 14l5.6-2.8" strokeLinecap="round" />
          </svg>
        </span>

        <span className="lb">{label}</span>

        {here !== null && <span className="qt">{num(here)}</span>}

        <span className="cv" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="m4 6.5 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {/* ฉากหลังหรี่ใช้เฉพาะจอแคบที่แผงกลายเป็นแผ่นล่าง จอกว้าง CSS ซ่อนไว้
          มีไว้ให้แตะที่ว่างแล้วปิดได้ ซึ่งเป็นท่าที่คนคาดหวังจากแผ่นแบบนี้ */}
      {open && <div className="ocv-scope-veil" onClick={() => setOpen(false)} />}

      {open && (
        <div className="ocv-scope-panel" onKeyDown={onKey}>
          <div className="hd">
            <span className="gr" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6">
                <circle cx="7" cy="7" r="4.6" />
                <path d="m10.6 10.6 3 3" strokeLinecap="round" />
              </svg>
            </span>
            <input
              ref={field}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls="ocv-scope-list"
              aria-autocomplete="list"
              aria-activedescendant={flat[active] ? rowId(active) : undefined}
              aria-label="ค้นหาส่วนราชการหรือกลุ่มภารกิจ"
              placeholder="พิมพ์ชื่อกรมหรือกลุ่มภารกิจ"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="button" className="cl" onClick={() => setOpen(false)}>
              esc
            </button>
          </div>

          <div className="cols" aria-hidden="true">
            <span>หน่วยงาน</span>
            <span>กรอบ · ว่าง</span>
          </div>

          <div className="lst" id="ocv-scope-list" role="listbox" aria-label="เลือกขอบเขตที่จะดู" ref={listBox}>
            {flat.length === 0 ? (
              <p className="none">ไม่พบหน่วยงานที่ตรงกับ “{query.trim()}”</p>
            ) : (
              /* แบนทั้งรายการ ไม่ห่อกลุ่มด้วย <div> — role="option" ต้องเป็นลูกตรงของ role="listbox"
                 ถ้าห่อ โปรแกรมอ่านหน้าจอจะไม่นับว่าแถวพวกนี้เป็นตัวเลือกของรายการนี้
                 ระยะห่างระหว่างกลุ่มจึงทำที่ .rw.t-group ใน CSS แทน */
              flat.map(drawRow)
            )}
          </div>

          <div className="ft">
            <span>↑↓ เลื่อน · Enter เลือก</span>
            <span>{num(flat.filter((r) => r.tier !== 'all' && r.tier !== 'group').length)} หน่วยงาน</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- แถบล่างสุด ---------------- */


/* ---------------- แถบตัวเลขรวมด้านบน ---------------- */

interface Stats {
  total: number;
  occupied: number;
  vacant: number;
  /** ผู้ที่จะเกษียณในปีงบประมาณปัจจุบัน */
  retiringNow: number;
  /** ปีงบประมาณปัจจุบัน (พ.ศ.) ใช้พิมพ์ลงป้าย ไม่ให้หน้าจอเดาเอง */
  fiscalYear: number;
  units: number;
  /** บรรทัดรองของช่อง "ว่าง" — ต่างกันตามว่ากำลังดูทั้งกระทรวงหรือเจาะหน่วยเดียว */
  vacantNote: string;
  retireNote: string;
}

/* ================================================================
   ตัวกรองจากกล่องตัวเลขด้านบน
   ================================================================ */

/**
 * กล่องตัวเลขสี่ใบบนแถบด้านบนกดได้ และกดแล้วตัดผังให้เหลือเฉพาะหน่วยที่เข้าเงื่อนไข
 *
 * ────────────────────────────────────────────────────────────────
 *  กฎข้อเดียว ไม่มีข้อยกเว้น
 * ────────────────────────────────────────────────────────────────
 *
 * กดกล่องไหน = "แสดงเฉพาะหน่วยที่มีตัวเลขในกล่องนั้นมากกว่าศูนย์"
 *
 *   กรอบอัตรา      ทุกหน่วยมีอัตราอยู่แล้ว จึงเท่ากับดูทั้งหมด (ใช้เป็นปุ่มล้างตัวกรองไปในตัว)
 *   มีผู้ครอง       ตัดหน่วยที่ว่างทั้งหน่วยออก
 *   ว่าง            เหลือเฉพาะหน่วยที่มีอัตราว่าง
 *   จะเกษียณใน 5 ปี  เหลือเฉพาะหน่วยที่มีคนจะเกษียณ
 *
 * ที่ยอมให้กล่องแรกไม่ตัดอะไรเลย ดีกว่าไปตั้งกฎพิเศษให้มัน เพราะกฎเดียวที่ใช้ได้
 * กับทุกกล่องอธิบายได้ในประโยคเดียว ส่วนกฎที่มีข้อยกเว้นสี่ข้อ ผู้ใช้ต้องจำ
 *
 * ⚠ ตัวเลขในกล่องต้องไม่เปลี่ยนตามตัวกรอง
 * มันคือยอดของขอบเขตที่กำลังดู ซึ่งเป็นตัวตั้งที่ใช้เทียบ ถ้ากดแล้วเลขในกล่องเปลี่ยนตาม
 * ช่อง "ว่าง" จะกลายเป็นว่างของหน่วยที่มีว่าง ซึ่งเท่ากับตัวมันเองเสมอ และอ่านไม่ได้ความ
 */
/** ป้ายชั้นภาษาไทย สำหรับคอลัมน์ "ชั้น" ในไฟล์ที่ส่งออก */
const KIND_TH: Record<OrgNode['kind'], string> = {
  ministry: 'กระทรวง',
  missionGroup: 'กลุ่มภารกิจ',
  department: 'ส่วนราชการ',
  unit: 'หน่วยงานภายใน',
};

export type StatFilter = 'total' | 'occupied' | 'vacant' | 'retiring';

const FILTER_NOTE: Record<StatFilter, string> = {
  total: '',
  occupied: 'เฉพาะหน่วยที่มีผู้ครองอย่างน้อยหนึ่งอัตรา',
  vacant: 'เฉพาะหน่วยที่มีอัตราว่าง',
  retiring: 'เฉพาะหน่วยที่มีผู้เกษียณรอบ 1 ต.ค. นี้',
};

/**
 * ป้ายกำกับตัวกรองสำหรับ "รายการอัตรา" ในแผงด้านขวา
 *
 * ⚠ แยกจาก FILTER_NOTE ข้างบนโดยตั้งใจ อันนั้นพูดถึง *หน่วยงาน* ("เฉพาะหน่วยที่มีอัตราว่าง")
 *   ส่วนอันนี้พูดถึง *อัตรา* ("เฉพาะอัตราว่าง") ถ้าใช้ข้อความชุดเดียวกันทั้งสองที่
 *   ผู้ใช้จะอ่านหัวรายการแล้วนึกว่ากำลังนับหน่วยงาน
 *
 * ⚠ ช่อง retiring ต้องพิมพ์ปีงบลงไปด้วย เพราะตัวกรองนี้คือ *ปีงบปัจจุบัน*
 *   แต่ตัวเลขในแผงเดียวกันคือ "เกษียณใน 5 ปี" สองอันนี้ไม่เท่ากันและอยู่ห่างกันไม่ถึงนิ้ว
 */
function posFilterLabel(f: StatFilter, fy: number): string {
  if (f === 'occupied') return 'เฉพาะอัตราที่มีผู้ครอง';
  if (f === 'vacant') return 'เฉพาะอัตราว่าง';
  if (f === 'retiring') return `เฉพาะผู้เกษียณ 1 ต.ค. ${fy}`;
  return '';
}

/**
 * ยอดของหน่วยตามตัวกรอง — ใช้ค่าที่รวมหน่วยย่อยมาแล้ว ไม่ต้องเดินซ้ำ
 *
 * ⚠ "เกษียณ" ที่นี่คือ *ปีงบประมาณปัจจุบัน* ไม่ใช่ภายใน 5 ปี
 *   ต้องตรงกับตัวเลขบนกล่อง KPI ที่ผู้ใช้เพิ่งกด ไม่งั้นกดแล้วผังเหลือหน่วยชุดหนึ่ง
 *   แต่ตัวเลขบนกล่องบอกอีกจำนวนหนึ่ง แล้วจะไม่มีใครรู้ว่าอันไหนคือความจริง
 */
function measureOf(n: OrgNode, f: StatFilter, fy: number): number {
  if (f === 'occupied') return n.occupied;
  if (f === 'vacant') return n.vacant;
  if (f === 'retiring') return n.retiringByYear?.[fy] ?? 0;
  return n.total;
}

/** อัตราหนึ่งอัตราเข้าเงื่อนไขไหม — ใช้กรองการ์ดตำแหน่งที่กางอยู่ใต้หน่วย */
function positionMatches(p: Position, f: StatFilter, fy: number): boolean {
  if (f === 'occupied') return !p.vacant;
  if (f === 'vacant') return p.vacant;
  if (f === 'retiring') return !p.vacant && p.incumbent?.retireFiscalYear === fy;
  return true;
}

/**
 * ตัดผังให้เหลือเฉพาะกิ่งที่เข้าเงื่อนไข
 *
 * ⚠ ใช้ยอดที่รวมหน่วยย่อยแล้ว (roll-up) ในการตัด ไม่ใช่ยอดของหน่วยนั้นเอง
 * ยอดรวมของพ่อไม่มีทางน้อยกว่าของลูก การตัดจึงไม่มีวันตัดพ่อของกิ่งที่ต้องเก็บไว้
 * ถ้าไปใช้ยอดเฉพาะตัว สำนักที่ตัวเองไม่มีอัตราว่างแต่มีส่วนย่อยที่ว่างจะหายไปทั้งกิ่ง
 * พร้อมกับส่วนย่อยที่ผู้ใช้กำลังตามหา
 */
function pruneTree(nodes: OrgNode[], f: StatFilter, fy: number): OrgNode[] {
  if (f === 'total') return nodes;
  const keep = (n: OrgNode): OrgNode | null => {
    if (measureOf(n, f, fy) <= 0) return null;
    return {
      ...n,
      positions: (n.positions ?? []).filter((p) => positionMatches(p, f, fy)),
      children: (n.children ?? []).map(keep).filter((c): c is OrgNode => c !== null),
    };
  };
  return nodes.map(keep).filter((n): n is OrgNode => n !== null);
}

/** ระดับที่ถือว่าสำคัญ ใช้ชุดคำเดียวกับที่ OrgFlow ใช้ตัดสินโหมดเตรียมผู้สืบทอด */
const SENIOR_WORDS = ['บริหาร', 'อำนวยการ', 'ทรงคุณวุฒิ', 'เชี่ยวชาญ'];

function summarize(scoped: OrgNode | null, summary: Summary): Stats {
  // ดูทั้งกระทรวง = ใช้ตัวเลขจากเซิร์ฟเวอร์ตรง ๆ เป็นตัวตั้งที่กระทบยอดได้
  if (!scoped) {
    return {
      total: summary.totalPositions,
      occupied: summary.occupied,
      vacant: summary.vacant,
      retiringNow: summary.retiringThisFiscalYear,
      fiscalYear: summary.fiscalYear,
      units: summary.units,
      vacantNote: `มีงบแล้ว ${num(summary.vacantFunded)} · ยังไม่มีงบ ${num(summary.vacantUnfunded)}`,
      // บอกยอดสะสมห้าปีไว้เป็นบรรทัดรอง เพราะเลขหลักเปลี่ยนไปนับเฉพาะปีงบนี้แล้ว
      // ถ้าไม่บอก ผู้ใช้ที่เคยเห็น 624 จะคิดว่าข้อมูลหาย
      retireNote: `ภายใน 5 ปี ${num(summary.retiringIn5Years)}`,
    };
  }

  let units = 0;
  let seniorVacant = 0;
  const walk = (n: OrgNode) => {
    units += 1;
    for (const p of n.positions ?? []) {
      if (p.vacant && SENIOR_WORDS.some((w) => p.level.includes(w))) seniorVacant += 1;
    }
    for (const c of n.children ?? []) walk(c);
  };
  walk(scoped);

  return {
    total: scoped.total,
    occupied: scoped.occupied,
    vacant: scoped.vacant,
    /*
     * ⚠ อ่านจาก retiringByYear ของโหนด ไม่ใช่เดินนับเองจาก positions
     *   ยอดในโหนดรวมหน่วยย่อยทุกชั้นมาแล้วจากฝั่งเซิร์ฟเวอร์
     *   ส่วน positions มีเฉพาะอัตราที่สังกัดหน่วยนั้นโดยตรง นับเองจะได้ยอดต่ำกว่าความจริง
     */
    retiringNow: scoped.retiringByYear?.[summary.fiscalYear] ?? 0,
    fiscalYear: summary.fiscalYear,
    units,
    // ⚠ ไม่มี "มีงบ/ยังไม่มีงบ" ในโครงสร้างผัง — มีเฉพาะในสรุปฝั่งเซิร์ฟเวอร์
    // จึงเปลี่ยนไปบอกสิ่งที่นับได้จริงจากผังแทน ไม่ใช่เอาสัดส่วนของทั้งกระทรวงมาคูณเดา
    vacantNote: `ว่างในระดับสำคัญ ${num(seniorVacant)}`,
    retireNote: `ภายใน 5 ปี ${num(scoped.retiringIn5Years)}`,
  };
}

/**
 * สี่ตัวเลขที่ผู้บริหารถามก่อนเสมอ
 *
 * ต่างจาก KpiRow ของหน้าภาพรวมเดิมตรงที่แถบนี้เตี้ยกว่ามาก เพราะต้องแบ่งความสูงให้ผัง
 * ตัวเลขจึงเล็กลง แต่ยังใหญ่กว่าตัวหนังสือรอบข้างชัดเจนพอที่จะกวาดตาเจอในครั้งเดียว
 */
/**
 * แถบล่างสุด — เหลือแค่บรรทัดลิขสิทธิ์
 *
 * เดิมแถบนี้มีที่มาข้อมูลอยู่ฝั่งซ้ายด้วย ("ที่มา: ทะเบียนตำแหน่ง DPIS (rpt_position) …
 * ส่วนชื่อบุคคลและวันเกิดในชุดข้อมูลนี้เป็นข้อมูลสมมุติ") ซึ่งย้ายมาจาก SiteFooter
 * ตอนที่ทั้งระบบเหลือหน้าเดียวที่ไม่เลื่อน — เอาออกตามที่ผู้ใช้ขอ
 *
 * ⚠ แปลว่าตอนนี้ไม่มีอะไรบนหน้าจอนี้บอกว่าชื่อบุคคลที่เห็นเป็นข้อมูลสมมุติ
 *   ตราบใดที่ยังรันด้วยชุดสาธิต (summary.synthetic) คำเตือนนั้นเหลืออยู่ที่เดียว
 *   คือใน warnings ของ API ซึ่งไม่ได้แสดงบนหน้านี้
 *   ถ้าระบบจะถูกใช้จริงโดยคนนอกทีม ต้องหาที่ใหม่ให้ประโยคนั้นก่อน
 */
function FootBar() {
  return (
    <footer className="ocv-foot">
      {/* ⚠ เรียก ownerLine() ไม่ใช่พิมพ์ชื่อซ้ำที่นี่ — สองที่ในระบบที่แสดงบรรทัดนี้
          (ท้ายหน้าปกติ กับท้ายหน้าของผัง) ต้องเปลี่ยนพร้อมกันเสมอ
          เดิมทั้งสองที่พิมพ์สตริงเดียวกันไว้เอง แล้ววันที่เปลี่ยนชื่อเจ้าของงาน
          ก็มีที่หนึ่งถูกลืมจนต้องมาไล่หาทีหลัง */}
      <span>{ownerLine()}</span>
    </footer>
  );
}

/**
 * แถบตัวเลขด้านบน — สี่ใบนี้กดได้ และกดแล้วตัดผังตามที่กด
 *
 * ⚠ เป็น <button> จริง ไม่ใช่ <div onClick>
 * กล่องพวกนี้เปลี่ยนสิ่งที่ทั้งหน้าจอแสดง จึงต้องกดด้วยคีย์บอร์ดได้ มีสถานะกดค้าง
 * ที่โปรแกรมอ่านหน้าจอบอกได้ (aria-pressed) และมีวงโฟกัสตอนไล่ด้วย Tab
 *
 * ⚠ ตัวเลขในกล่องไม่เปลี่ยนตามตัวกรอง — ดูเหตุผลที่ StatFilter ด้านบน
 */
function StatStrip({
  stats,
  filter,
  onFilter,
}: {
  stats: Stats;
  /** null = ยังไม่ได้กรอง ไม่มีใบไหนติด */
  filter: StatFilter | null;
  onFilter: (next: StatFilter | null) => void;
}) {
  const filled = stats.total > 0 ? stats.occupied / stats.total : 0;
  const band = bandOf(filled);

  const cells: Array<{
    id: StatFilter;
    l: string;
    v: string;
    n: string;
    tone?: string;
  }> = [
    { id: 'total', l: 'กรอบอัตรากำลัง', v: num(stats.total), n: `${num(stats.units)} หน่วยงาน` },
    {
      id: 'occupied',
      l: 'มีผู้ครอง',
      v: num(stats.occupied),
      n: `${Math.round(filled * 100)}% ของกรอบ · ${band.label}`,
      tone: band.color,
    },
    { id: 'vacant', l: 'อัตราว่าง', v: num(stats.vacant), n: stats.vacantNote, tone: 'var(--alert)' },
    {
      id: 'retiring',
      l: `เกษียณ 1 ต.ค. ${stats.fiscalYear}`,
      v: num(stats.retiringNow),
      n: stats.retireNote,
      tone: 'var(--gold-ink)',
    },
  ];

  return (
    <div className="ocv-stats" role="group" aria-label="ตัวเลขรวม กดเพื่อกรองผัง">
      {cells.map((c) => {
        const on = filter === c.id;
        const what = c.id === 'total' ? 'แสดงทุกหน่วยงาน' : `แสดง${FILTER_NOTE[c.id]}`;
        return (
          <button
            type="button"
            className={`ocv-stat${on ? ' is-on' : ''}`}
            key={c.id}
            aria-pressed={on}
            /* บอกให้ครบว่ากดแล้วได้อะไร — ตัวเลขกับป้ายในกล่องไม่ได้บอกเรื่องนี้เลย
               และต้องบอกด้วยว่ากดซ้ำแล้วเลิกกรอง ซึ่งเดาจากหน้าตาไม่ได้ */
            title={on ? 'กดอีกครั้งเพื่อเลิกกรอง' : what}
            aria-label={`${c.l} ${c.v} — ${on ? 'กำลังกรองอยู่ กดเพื่อเลิกกรอง' : `กดเพื่อ${what}`}`}
            onClick={() => onFilter(on ? null : c.id)}
          >
            <span className="l">{c.l}</span>
            <span className="v tnum" style={c.tone ? { color: c.tone } : undefined}>{c.v}</span>
            <span className="n">{c.n}</span>
            {/* ไอคอนบอกว่าใบนี้กดได้ — ไม่มีอะไรในกล่องบอกเรื่องนี้เลย
                มันหน้าตาเหมือนป้ายตัวเลขทุกประการ ผู้ใช้จึงไม่เคยลองกด
                จางไว้ตอนปกติ เข้มตอนชี้ และเป็นสีทองตอนกดค้าง (ดู .ocv-stat .fi) */}
            <FilterOutlined className="fi" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- คำอธิบายสัญลักษณ์ ---------------- */

/**
 * คำอธิบายสัญลักษณ์ — แถบเดียวบาง ๆ มุมล่างซ้าย ยุบเป็นไอคอน i ได้
 *
 * เว็บต้นแบบวางแผงคำอธิบายเป็นกล่องสูงทับบนผังตลอดเวลา แล้วปิดได้อย่างเดียว เปิดกลับไม่ได้
 * ซึ่งบังคับให้ผู้ใช้เลือกระหว่าง "เห็นผังไม่ครบ" กับ "ไม่รู้ว่าสีแปลว่าอะไรอีกเลย"
 *
 * ที่นี่ย่อเหลือแถวเดียวสูง 34px จึงแทบไม่เคยบังการ์ด และยุบเหลือไอคอนได้ถ้าอยากได้พื้นที่คืน
 * ส่วนคำอธิบายแบบเต็มประโยคย้ายไปอยู่บรรทัดใต้ปุ่มโหมด ซึ่งเป็นที่ที่ต้องอ่านก่อนดูผังอยู่แล้ว
 *
 * ⚠ ปุ่มยุบต้องคงอยู่เสมอ ห้ามทำเป็น "ปิดแล้วหายไปเลย"
 * ผังต้นแบบที่เอามาเทียบทำแบบนั้น แล้วผู้ใช้ที่เผลอกดปิดจะไม่มีทางรู้ว่าสีบนการ์ด
 * แปลว่าอะไรอีกเลยตลอดทั้ง session
 *
 * ⚠ ไอคอนอย่างเดียวไม่มีข้อความ สิ่งที่บอกว่าปุ่มนี้คืออะไรจึงเหลือแค่ aria-label
 * กับ title เท่านั้น ห้ามเอาสองอย่างนั้นออกตามข้อความไปด้วย
 */
function Legend({ mode, open, onToggle }: { mode: OrgFlowMode; open: boolean; onToggle: () => void }) {
  if (!open) {
    return (
      <button
        type="button"
        className="ocv-legend-pin"
        onClick={onToggle}
        aria-expanded={false}
        aria-label="คำอธิบายสัญลักษณ์ — กดเพื่อดูว่าสีบนการ์ดแปลว่าอะไร"
        title="คำอธิบายสัญลักษณ์"
      >
        <InfoOutlined aria-hidden="true" />
      </button>
    );
  }

  const chips =
    mode === 'succession'
      ? [
          { c: 'var(--alert)', t: 'มีตำแหน่งระดับสูงที่จะว่าง' },
          { c: 'var(--brand)', t: 'ไม่มีตำแหน่งระดับสูงที่จะว่าง' },
        ]
      : mode === 'retirement'
        ? [
            { c: 'var(--gold)', t: 'มีผู้เกษียณอายุราชการภายใน 5 ปี' },
            { c: 'var(--brand)', t: 'ไม่มีผู้เกษียณอายุราชการในช่วงนี้' },
          ]
        : FILL_BANDS.map((b) => ({
            c: b.color,
            t: `${b.label} ${b.key === 'ok' ? '≥ 90%' : b.key === 'watch' ? '60–90%' : '< 60%'}`,
          }));

  return (
    <div className="ocv-legend">
      {chips.map((c) => (
        <span className="chip" key={c.t}>
          <i style={{ background: c.c }} />
          {c.t}
        </span>
      ))}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={true}
        aria-label="ยุบคำอธิบายสัญลักษณ์"
        title="ยุบคำอธิบายสัญลักษณ์"
      >
        −
      </button>
    </div>
  );
}

/* ---------------- แผงรายละเอียดหน่วยงาน ---------------- */

/**
 * แผงขวา — บอกเรื่องของ "หน่วยที่กด" ให้ครบโดยไม่ต้องเปลี่ยนหน้า
 *
 * ⚠ ยอดในแผงนี้เป็นยอดรวมหน่วยย่อยทุกชั้น เท่ากับที่การ์ดบอก
 * ส่วนรายการตำแหน่งด้านล่างเป็นตำแหน่งที่สังกัดหน่วยนี้ **โดยตรง** เท่านั้น
 * เขียนกำกับไว้บนหัวรายการแล้ว เพราะสองยอดนี้ไม่เท่ากันโดยตั้งใจ
 * และเป็นจุดที่ผู้ใช้จะคิดว่าระบบนับผิดถ้าไม่บอก
 */
function UnitDrawer({
  unit,
  mode,
  filter,
  fiscalYear,
  onClose,
  onOpenPosition,
}: {
  unit: OrgNode;
  mode: OrgFlowMode;
  /**
   * ตัวกรองที่มาจากกล่องตัวเลขสี่กล่องด้านบน — รายการอัตราในแผงต้องเดินตามมันด้วย
   *
   * ⚠ ตัวเลขสามช่องบนหัวแผง (มีผู้ครอง/ว่าง/เกษียณใน 5 ปี) **ไม่** เปลี่ยนตามตัวกรอง
   *   ด้วยเหตุผลเดียวกับกล่อง KPI ด้านบน (ดูหัวข้อ StatFilter) มันคือตัวตั้งที่ใช้เทียบ
   *   สิ่งที่เปลี่ยนคือรายการด้านล่าง และต้องเขียนกำกับไว้ว่ากรองอยู่ ไม่งั้นผู้ใช้จะเห็น
   *   "ว่าง 118" อยู่ข้างบน แต่รายการมี 118 แถวพอดี แล้วสรุปว่าหน่วยนี้ว่างทั้งหน่วย
   */
  filter: StatFilter;
  /** ปีงบของตัวกรอง "เกษียณ" — ต้องเป็นปีเดียวกับที่กล่อง KPI แสดง */
  fiscalYear: number;
  onClose: () => void;
  /**
   * ⚠ หน้าต่างรายละเอียดอัตราต้องถูกเรนเดอร์ที่ตัวครอบ ไม่ใช่ในแผงนี้
   *
   * แผงนี้มี position:relative + z-index เพื่อให้เงาของมันทับผังได้
   * ซึ่งสร้าง stacking context ขึ้นมาหนึ่งชั้น อะไรที่อยู่ข้างในจึงถูกกดให้อยู่ในระดับของแผง
   * ต่อให้ตั้ง z-index:200 ก็ตาม — หน้าต่างเลยไปโผล่ "ใต้" แถบชื่อระบบด้านบน
   *
   * ทางแก้ที่ถูกคือส่งขึ้นไปให้ตัวครอบเรนเดอร์เป็นพี่น้องกับแถบบน
   * (ไม่ใช้ portal ไป document.body เพราะตอนกดเต็มจอ เบราว์เซอร์วาดเฉพาะกิ่งของ .ocv
   *  อะไรที่ portal ออกไปนอกกิ่งนั้นจะหายไปทั้งอัน)
   */
  onOpenPosition: (p: Position) => void;
}) {
  /* filled / band กับ years ถูกเอาออกพร้อมแถบสถานะและรายการเกษียณรายปี (ดูหมายเหตุในเนื้อแผง)
     left / leftBand ยังใช้อยู่ — โหมด forecast ด้านล่างเรียกทั้งคู่ */
  const left = Math.max(unit.occupied - unit.retiringIn5Years, 0);
  const leftBand = bandOf(unit.total > 0 ? left / unit.total : 0);

  /**
   * รายชื่อตำแหน่ง "ทั้งสาย" ของหน่วยนี้ ไม่ใช่เฉพาะที่สังกัดโดยตรง
   *
   * ⚠ เดิมแสดงแค่ unit.positions ซึ่งเป็นอัตราที่สังกัดหน่วยนี้ตรง ๆ
   *   ผลคือกดการ์ดกระทรวงหรือกลุ่มภารกิจแล้วได้ "0 อัตรา" ทุกครั้ง
   *   เพราะสองชั้นนั้นเป็นชั้นจัดกลุ่ม ไม่มีอัตราสังกัดโดยตรงเลยสักอัตรา
   *   ขณะที่หัวแผงบรรทัดบนบอกว่า "370 อัตรา (รวมหน่วยย่อยทุกชั้น)" อยู่
   *   สองยอดนี้อยู่บนแผงเดียวกันและขัดกันเอง ซึ่งอ่านออกได้ว่าระบบนับผิด
   *
   * ⚠ เรียงตามความอาวุโสข้ามหน่วย ไม่ใช่ตามลำดับของผัง
   *   คนที่เปิดแผงของทั้งกลุ่มภารกิจต้องการเห็นคนระดับสูงก่อน ไม่ใช่ไล่ตามกิ่ง
   *   levelRank มาจาก @shared/levels ซึ่งเป็นชุดเดียวกับที่เซิร์ฟเวอร์ใช้เรียง
   *   ห้ามเขียนลำดับระดับซ้ำไว้ในไฟล์นี้ ไม่งั้นสองที่จะเลื่อนออกจากกันเงียบ ๆ
   */
  const all = useMemo(() => {
    const out: Position[] = [];
    const walk = (n: OrgNode) => {
      for (const p of n.positions ?? []) out.push(p);
      for (const k of n.children ?? []) walk(k);
    };
    walk(unit);
    out.sort(
      (a, b) =>
        levelRank(a.level) - levelRank(b.level) ||
        a.unit.localeCompare(b.unit, 'th') ||
        a.title.localeCompare(b.title, 'th'),
    );
    return out;
  }, [unit]);

  /* รายการที่แสดงจริง = อัตราในสายนี้ ที่ผ่านตัวกรองจากกล่องตัวเลขด้านบน
     ⚠ ใช้ positionMatches ตัวเดียวกับที่ pruneTree ใช้ตัดผัง ห้ามเขียนเงื่อนไขซ้ำที่นี่
       ไม่งั้นวันหนึ่งผังกับแผงจะตอบไม่ตรงกันเงียบ ๆ ว่าอัตราไหน "ว่าง" */
  const own = useMemo(
    () => all.filter((p) => positionMatches(p, filter, fiscalYear)),
    [all, filter, fiscalYear],
  );

  const narrowed = filter !== 'total';
  const ownVacant = all.filter((p) => p.vacant).length;
  const ownRetiring = all.filter((p) => retireMark(p.incumbent) !== null).length;

  /* เพดานของรายการ — กรมใหญ่มีสองพันกว่าอัตรา ถ้าวาดหมดแผงจะค้างตอนเปิด
     ⚠ ถูกตัดแล้วต้องบอกเสมอว่าตัดไปเท่าไร ห้ามแสดงรายการสั้น ๆ เฉย ๆ
       เพราะผู้ใช้จะนับจากรายการแล้วสรุปว่าหน่วยนี้มีแค่นั้น */
  const LIST_MAX = 300;
  const shown = own.slice(0, LIST_MAX);

  return (
    <aside className="ocv-panel" aria-label={`รายละเอียดของ ${unit.name}`}>
      <div className="ocv-panel-hd">
        <div>
          <b>{unit.name}</b>
          <span>{num(unit.total)} อัตรา (รวมหน่วยย่อยทุกชั้น)</span>
        </div>
        {/* เดิมมีปุ่ม "ดูเฉพาะสายบังคับบัญชานี้" (.ocv-only) ตรงนี้ เอาออกตามที่ผู้ใช้ขอ

            การตัดผังให้เหลือเฉพาะสายที่เลือกยังทำได้อยู่สองทาง —
            คลิกการ์ดในผัง (lineageIds ใน OrgFlow.tsx ตัดให้อัตโนมัติ) และช่องเลือกขอบเขต
            ด้านบน ปุ่มนี้จึงเป็นทางที่สาม ซึ่งซ้ำกับสองทางแรก
            ถ้าจะเอากลับ ต้องคืน prop onScopeTo ให้ UnitDrawer ด้วย ตัวจัดการ setScope
            ที่ตัวครอบยังอยู่ครบ */}
        <button type="button" onClick={onClose} aria-label="ปิดแผงข้อมูลส่วนราชการ">✕</button>
      </div>

      <div className="ocv-panel-body">
        {/*
          ⚠ ตีกรอบเฉพาะช่องที่ยอดของมัน "เท่ากับ" จำนวนแถวในรายการด้านล่างพอดี
            มีผู้ครอง/ว่าง เท่ากันจริง เพราะทั้งคู่นับจากอัตราในสายนี้ชุดเดียวกัน
            แต่ "เกษียณใน 5 ปี" ไม่เท่า — ตัวกรองเกษียณเป็นของปีงบปัจจุบันปีเดียว
            ถ้าไปตีกรอบช่องนั้นด้วย จะกลายเป็นชี้ว่ารายการข้างล่างมี 96 แถว ทั้งที่มี 12
        */}
        <dl className="ocv-figs">
          <div className={filter === 'occupied' ? 'is-on' : undefined}>
            <dt>มีผู้ครอง</dt>
            <dd className="tnum">{num(unit.occupied)}</dd>
          </div>
          <div className={filter === 'vacant' ? 'is-on' : undefined}>
            <dt>ว่าง</dt>
            <dd className="tnum is-alert">{num(unit.vacant)}</dd>
          </div>
          <div>
            <dt>เกษียณใน 5 ปี</dt>
            <dd className="tnum">{num(unit.retiringIn5Years)}</dd>
          </div>
        </dl>

        {/* เดิมมีแถบสถานะ "N% · เฝ้าระวัง/ปกติ/วิกฤต" อยู่ตรงนี้ เอาออกตามที่ผู้ใช้ขอ

            ⚠ ความหมายของสีไม่ได้หายไปจากหน้า — การ์ดในผังยังระบายตามแถบเดิม
              และคำอธิบายสัญลักษณ์มุมซ้ายล่างยังบอกเกณฑ์ครบ
              ถ้าจะเอากลับ ใส่ <div className="ocv-band"> คืน แล้วคำนวณ filled/bandOf ตรงหัวฟังก์ชัน
              CSS ของ .ocv-band ยังอยู่ครบใน styles.css */}

        {mode === 'forecast' && (
          <p className="ocv-block">
            ถ้าไม่บรรจุทดแทนเลย ครบห้าปีหน่วยนี้จะเหลือ <b className="tnum">{num(left)}</b> คน
            จากกรอบ <b className="tnum">{num(unit.total)}</b> อัตรา
            (<b>{Math.round((unit.total > 0 ? left / unit.total : 0) * 100)}%</b> — {leftBand.label})
            <em>ตัวเลขนี้มาจากวันเกษียณตามกฎ ก.พ. ของผู้ครองอัตราแต่ละคน ไม่ใช่การพยากรณ์</em>
          </p>
        )}

        {/* เดิมมีรายการ "ผู้เกษียณอายุราชการ จำแนกตามปีงบประมาณ" อยู่ตรงนี้ เอาออกตามที่ผู้ใช้ขอ

            ⚠ ยอดรวมยังอยู่ในช่อง "เกษียณใน 5 ปี" ด้านบน และรายชื่อรายคนพร้อมเดือนปีที่เกษียณ
              ยังอยู่ในรายการตำแหน่งด้านล่าง (ป้าย "เกษียณ ก.ย. 25xx") ที่หายไปคือการกระจายรายปี
              ข้อมูลต้นทาง unit.retiringByYear ยังส่งมาจากเซิร์ฟเวอร์เหมือนเดิม ไม่ได้ตัดที่ API
              CSS ของ .ocv-years ยังอยู่ครบใน styles.css */}

        <section className="ocv-sec">
          <h4>
            ตำแหน่งในสายนี้
            <span>
              {narrowed ? (
                <>
                  {/* บอกทั้งตัวเศษและตัวส่วนเสมอ — "118 อัตรา" เฉย ๆ อ่านไม่ออกว่ากรองอยู่ */}
                  <b className="ocv-filt">{posFilterLabel(filter, fiscalYear)}</b>
                  {num(own.length)} จาก {num(all.length)} อัตรา
                </>
              ) : (
                <>
                  {num(all.length)} อัตรา{ownVacant > 0 && ` · ว่าง ${num(ownVacant)}`}
                  {ownRetiring > 0 && ` · เกษียณใน ${RETIRE_WATCH_YEARS} ปี ${num(ownRetiring)}`}
                </>
              )}
            </span>
          </h4>
          {own.length === 0 ? (
            /* ⚠ แยกสองข้อความ — "ไม่มีอัตราในระบบ" ตอนที่จริง ๆ มีอัตราอยู่แต่ถูกกรองออกหมด
                 คือการบอกข้อมูลผิด และผู้ใช้จะสรุปว่าหน่วยนี้เป็นหน่วยเปล่า */
            <p className="ocv-empty">
              {narrowed && all.length > 0
                ? `ในสายนี้มี ${num(all.length)} อัตรา แต่ไม่มีอัตราไหนเข้าเงื่อนไข “${posFilterLabel(filter, fiscalYear)}” — กดกล่องตัวเลขใบเดิมซ้ำเพื่อเลิกกรอง`
                : 'หน่วยนี้ไม่มีอัตราในระบบ'}
            </p>
          ) : (
            <ul className="ocv-pos">
              {shown.map((p, i) => {
                const mark = retireMark(p.incumbent);
                return (
                  <li
                    key={`${p.posNo ?? 'no'}-${i}`}
                    className={p.vacant ? 'is-vacant' : mark ? `is-${mark.tone}` : undefined}
                  >
                    {/* ทั้งแถวเป็นปุ่ม ไม่ใช่ลิงก์เล็ก ๆ ท้ายบรรทัด — เป้ากดใหญ่กว่ามากและใช้บนจอสัมผัสได้
                        ใช้ <button> จริงเพื่อให้ไล่ด้วย Tab และกด Enter ได้เหมือนกัน */}
                    <button type="button" onClick={() => onOpenPosition(p)}>
                      <b>{p.title}</b>
                      <span className="lv">
                        {p.level || 'ไม่ระบุระดับ'}
                        {/* บอกหน่วยเฉพาะเมื่อไม่ใช่หน่วยที่เปิดแผงอยู่ — ถ้าใส่ทุกแถว
                            แผงของหน่วยเล็ก ๆ จะมีชื่อหน่วยเดิมซ้ำทุกบรรทัดโดยไม่ได้บอกอะไรใหม่ */}
                        {p.unit && p.unit !== unit.name && <em>{p.unit}</em>}
                      </span>
                      <span className="who">
                        {p.vacant ? <em>ว่าง</em> : (p.incumbent?.name ?? '—')}
                        {/* ป้ายอยู่ติดชื่อคน ไม่ใช่มุมขวาของแถว เพราะมันเป็นเรื่องของคนคนนั้น
                            ไม่ใช่สถานะของอัตรา (ซึ่งคือแถบสีซ้ายมือ) */}
                        {mark && <b className={`rt is-${mark.tone}`}>{mark.label}</b>}
                      </span>
                      <span className="go" aria-hidden="true">›</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {own.length > shown.length && (
            <p className="ocv-empty">
              แสดง {num(shown.length)} จาก {num(own.length)} อัตรา
              {narrowed && ` ที่เข้าเงื่อนไข “${posFilterLabel(filter, fiscalYear)}”`}
              {' '}— กดการ์ดของหน่วยย่อยเพื่อดูรายการที่เหลือ
            </p>
          )}
        </section>
      </div>
    </aside>
  );
}
